import * as api from "./api";
import { attachmentUrl, loadAttachment, uploadImage } from "./attachments";
import { clear, h } from "./dom";
import { captureContext, ensureBuild, getBuild } from "./environment";
import { getConsoleTail } from "./console-tail";
import {
  isPicking,
  isPickingMultiple,
  refreshPickMarkers,
  startPick,
  stopPick,
  type PickResult,
} from "./pick";
import { PANEL_CSS } from "./styles";
import { CHROME_ATTR } from "./anchor";
import {
  DEFAULT_ROUTE_KEY_CONFIG,
  toRouteKey,
  type RouteKeyConfig,
} from "../shared/route-key";
import { looksLikeTesterToken } from "../shared/token-shape";
import { formatFindingRef, parseFindingRef } from "../shared/finding-ref";
import { buildCarries, isSameBuild } from "../shared/build-match";
import type {
  ChecklistItem,
  ChecklistItemStatus,
  ChecklistState,
  Finding,
  FindingComment,
  FindingStepResult,
  ItemChatThread,
  QaSession,
  Severity,
  StepResultStatus,
  TesterStatus,
  WidgetConfig,
} from "../shared/schemas";

/**
 * The QA panel: a right-edge tab + a slide-in column, rendered into a CLOSED
 * shadow root so the host site's CSS can neither style it nor be styled by it.
 *
 * Pick-mode overlays deliberately live OUTSIDE this shadow root (see `pick.ts`).
 *
 * Ported from qa-deck's `src/widget/panel.ts`. The multi-tenant change is that
 * route normalization is no longer a compile-time constant: the project's
 * locales and default sub-views arrive with `GET /api/qa/config`, so the panel
 * boots on the neutral config and recomputes the current route key once the
 * real one lands.
 */

/** One open scenario-chat conversation. */
interface ChatView {
  item: ChecklistItem;
  /** Null ⇒ the scenario itself rather than one of its steps. */
  stepIndex: number | null;
  thread: ItemChatThread | null;
  loading: boolean;
  sending: boolean;
  error: string | null;
}

/** One finding's conversation, open under its card. */
interface ThreadView {
  comments: FindingComment[];
  loading: boolean;
  sending: boolean;
  error: string | null;
}

/**
 * The "All findings" tab's filter. `needs_me` is the job list — fixes to
 * retest, questions to decide, messages to answer — and the rest are plain
 * statuses, so a finding the tester answered by mistake is always one click
 * from its card.
 */
type AllFilter =
  "needs_me" | "open" | "fixed" | "resolved" | "wont_fix" | "everything";

/**
 * Why a finding is waiting on the tester — the three jobs "Needs me" groups
 * by. One colour each, the same the strip's pills use: blue is conversation,
 * violet a question, amber a fix claim.
 */
type TurnKind = "answer" | "decide" | "retest";
/** The two jobs that pile up — fifty of each is an ordinary week — and so get
 *  "Load more", "See all" and a search of their own. */
type PagedTurn = "answer" | "retest";
/** Rows "Need answer" / "Retest" show, and add per "Load more". */
const PAGED_STEP = 10;
/** Rows one page of the "See all" popup holds. */
const POPUP_PAGE = 100;

interface State {
  open: boolean;
  tab: "checklist" | "findings" | "all";
  routeKey: string;
  config: WidgetConfig | null;
  session: QaSession | null;
  items: ChecklistItem[];
  otherViewItems: ChecklistItem[];
  globalItems: ChecklistItem[];
  itemState: Map<string, ChecklistState>;
  /**
   * Scenarios whose latest verdict across the PROJECT is `failed` and whose
   * finding a developer has since marked fixed. Not derivable from `itemState`:
   * that is this session only, and a retest sweep starts empty.
   */
  retest: Set<string>;
  /** Queue size across every surface, so the filter can say what it hides. */
  retestTotal: number;
  /** Show only the retest queue. Off by default — a sweep is the normal job. */
  retestOnly: boolean;
  /** The session chooser is open over a running session. */
  sessionPicker: boolean;
  /**
   * The "what is this asking me to do?" conversation currently open, or null.
   *
   * A MODE rather than a tab: the chat is about one scenario, and showing it
   * beside the checklist would leave the tester reading two things at once —
   * the very confusion it exists to clear up.
   */
  chat: ChatView | null;
  /** Superseded wording, keyed by finding id — fetched only when asked for. */
  revisions: Map<string, api.FindingRevisionRow[]>;
  /**
   * Open conversations, keyed by finding id. Absent ⇒ collapsed; a card is
   * never fetched until someone asks for it, because a route can hold a dozen
   * findings and most of them have nothing said about them.
   */
  threads: Map<string, ThreadView>;
  /**
   * "New for you" — replies, fixes waiting for retest, a changed checklist.
   *
   * Null until the first read, and null FOREVER against a server that predates
   * the endpoint: the strip and the FAB badge are then simply absent, the same
   * way an absent `itemChatEnabled` means no ⓘ rather than a button that
   * errors.
   */
  inbox: api.TesterInbox | null;
  /** The strip is collapsible; "Dismiss" closes it and stamps the clock. */
  inboxOpen: boolean;
  /**
   * "n dismissed" is open: the strip also lists the claims this tester
   * dismissed, each with a way back. The undo for a mis-click on ✕ — and it
   * has to be reachable after a page load, so it is a list, not a toast.
   */
  inboxShowDismissed: boolean;
  /** The strip past its first few rows — asked for, never the default. */
  inboxShowAll: boolean;
  /**
   * The finding an inbox row is pointing at, pinned to the top of the tab.
   *
   * The strip names findings filed anywhere in the project; the Findings tab
   * lists only the page it is standing on. The first version of this navigated
   * to the page the finding was filed on — which cannot work when that page is
   * on another ORIGIN, and that is the ordinary case: a tester on localhost
   * answering a fix claim about a finding filed on staging. It also asked the
   * wrong thing of them, because the answer ("verified" / "still broken", or a
   * reply) lives on the CARD, not on the page.
   *
   * So the card comes to the tester: fetched by id and rendered above the
   * page's own list, wherever they are, with the URL it was filed on stated on
   * it. `finding` is null while the fetch is in flight or if it failed —
   * `error` is what the tab shows then, because a row that opens nothing and
   * says nothing is the dead end this exists to remove.
   */
  focus:
    | (api.FindingFocus & {
        finding: Finding | null;
        loading: boolean;
        error: string | null;
      })
    | null;
  /**
   * The filed finding currently loaded into the form at the top of the tab,
   * or null when that form is writing a new one. There is ONE finding form:
   * "edit" means putting an existing finding back into it, not opening a
   * second editor under the card, which is two places to look and the smaller
   * of the two. Tracker targeting rides along, because it is decided AT THE
   * TESTING MOMENT — the tester on the page knows the sprint and the story.
   */
  editingFindingId: string | null;
  issueTargets: {
    assignees: api.IssueTargetOption[];
    iterations: api.IssueTargetOption[];
    parents: api.IssueTargetOption[];
    types: api.IssueTargetOption[];
  } | null;
  /**
   * Where the one fetch of people / sprints / stories has got to.
   *
   * Three states rather than a boolean, because "we have asked" and "we have an
   * answer" are different questions. One flag answering both was set BEFORE the
   * await, so the whole time the tracker call was in flight the form rendered
   * its could-not-enumerate fallback — plain text boxes that look exactly like
   * a tracker whose PAT cannot list anything, rather than like loading.
   */
  issueTargetsStatus: "idle" | "loading" | "loaded";
  target: {
    assignee: string;
    iteration: string;
    parentId: string;
    workItemType: string;
  };
  newStory: string;
  /**
   * The queue is a PROJECT-wide read, so it must not ride along with every
   * route change the way the per-route checklist does. Loaded once and
   * invalidated when a verdict changes, which is the only thing that moves an
   * item in or out of it.
   */
  retestLoaded: boolean;
  findings: Finding[];
  /**
   * Every finding in the PROJECT, whatever page the panel is on — the tab that
   * makes nothing unreachable. The strip can be dismissed and the page list
   * only shows this page; this list can do neither. Rows are compact and open
   * into the full card (`open`), because a project holds hundreds.
   */
  all: {
    filter: AllFilter;
    query: string;
    findings: Finding[];
    loading: boolean;
    loaded: boolean;
    error: string | null;
    /** The server caps a read at 200; said when a list hits it. */
    capped: boolean;
    open: Set<string>;
    /** One job picked from the "Needs me" header; null shows all three. */
    kind: TurnKind | null;
    /** The one "Needs me" row unfolded to its message and verbs — one at a
     *  time, so the list stays a list however long it gets. */
    peek: string | null;
    /** Groups the tester folded away, and groups showing past the first few. */
    folded: Set<TurnKind | "answered">;
    showAll: Set<TurnKind | "answered">;
    /** "Need answer" and "Retest": how many rows are loaded, and the inline
     *  search, per group. */
    lists: Record<
      PagedTurn,
      { limit: number; searching: boolean; query: string }
    >;
    /** "See all" — one group in a scrollable popup, a hundred rows a page. */
    popup: { kind: PagedTurn; page: number; query: string } | null;
  };
  expanded: Set<string>;
  /**
   * Elements picked for the finding being composed, in pick order — the
   * first is filed as `anchor`, the rest as `more_elements`.
   */
  picks: PickResult[];
  /** Checklist item the new finding is linked to (set by "fail"). */
  linkedItemId: string | null;
  error: string | null;
  status: string | null;
  outbox: number;
  loading: boolean;
}

const state: State = {
  open: false,
  tab: "checklist",
  routeKey: "/",
  config: null,
  session: null,
  items: [],
  otherViewItems: [],
  globalItems: [],
  itemState: new Map(),
  retest: new Set(),
  retestTotal: 0,
  retestOnly: false,
  sessionPicker: false,
  chat: null,
  revisions: new Map(),
  threads: new Map(),
  inbox: null,
  inboxOpen: true,
  inboxShowDismissed: false,
  inboxShowAll: false,
  focus: null,
  editingFindingId: null,
  issueTargets: null,
  issueTargetsStatus: "idle",
  target: { assignee: "", iteration: "", parentId: "", workItemType: "" },
  newStory: "",
  retestLoaded: false,
  findings: [],
  all: {
    filter: "needs_me",
    query: "",
    findings: [],
    loading: false,
    loaded: false,
    error: null,
    capped: false,
    open: new Set(),
    kind: null,
    peek: null,
    folded: new Set(),
    showAll: new Set(),
    lists: {
      answer: { limit: PAGED_STEP, searching: false, query: "" },
      retest: { limit: PAGED_STEP, searching: false, query: "" },
    },
    popup: null,
  },
  expanded: new Set(),
  picks: [],
  linkedItemId: null,
  error: null,
  status: null,
  outbox: 0,
  loading: false,
};

/**
 * An array field as it ACTUALLY arrived on the wire.
 *
 * The widget imports the schemas as types only (rule 6), so nothing parses a
 * response: a field the server did not send is `undefined` at runtime while
 * TypeScript swears it is an array. That is not hypothetical — the bundle is a
 * static file served straight off disk and cached in a tester's browser, while
 * the API is a container that was built at some other moment, so the widget is
 * routinely NEWER than the server answering it. `step_results` shipped and the
 * next `.length` on it took the whole Findings tab down against a deployment
 * that predated the column.
 *
 * So every list the widget reads off a finding or a checklist state goes
 * through here. A missing field degrades to "none", which is exactly how an
 * older server should read — the same way an absent `itemChatEnabled` already
 * means "no ⓘ button" rather than a crash.
 */
function wireList<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}

/** Per-project normalization, replaced when `GET /api/qa/config` returns. */
let routeConfig: RouteKeyConfig = DEFAULT_ROUTE_KEY_CONFIG;

let root: ShadowRoot;
let bodyEl: HTMLElement;
let footEl: HTMLElement;
let panelEl: HTMLElement;
let fabEl: HTMLButtonElement;
let routeEl: HTMLElement;
let newSessionEl: HTMLButtonElement;
/** The Findings tab, so its label can carry the unread-reply count. */
let findingsTabEl: HTMLButtonElement;
/** The All findings tab, so its label can carry the "waiting on you" count. */
let allTabEl: HTMLButtonElement;
/**
 * Where a popup mounts: beside the panel in the shadow root, not inside it.
 * The panel is a fixed, z-indexed box — its own stacking context — so
 * anything inside it, however high its z-index, stays under the edge tab.
 */
let popupEl: HTMLElement;
/**
 * Reply drafts, keyed by finding id, kept across re-renders for the same
 * reason the finding draft is: a re-render must never throw away words someone
 * has already typed.
 */
const commentDrafts = new Map<string, string>();
/** Draft of the new-finding form, kept across re-renders. */
const draft = {
  severity: "medium" as Severity,
  observed: "",
  expected: "",
  comment: "",
  /** Attachment ids already uploaded and waiting for this finding to claim
   *  them — see `server/attachments.ts` for why they exist before it does. */
  attachments: [] as string[],
  /**
   * The linked scenario's steps, with how each one went. Seeded from the
   * scenario when ✕ opens this form and sent as `step_results`; the TEXT rides
   * along because a finding records the words the tester read, not a pointer
   * into a spec that will be rewritten.
   */
  stepResults: [] as FindingStepResult[],
  /** File to the tracker as part of saving. Off by default — most findings are
   *  a sweep's notes, not a bug someone must action today — but touching any
   *  target field turns it on, because that is what picking a sprint means. */
  fileToTracker: false,
};

/**
 * An unfinished NEW finding, parked while the one form is borrowed to edit a
 * filed one, and put back when that edit ends. Without it, clicking Edit on
 * someone's finding would silently throw away words this tester had already
 * typed — the one thing the panel exists to protect.
 */
let parkedDraft: {
  draft: typeof draft;
  picks: PickResult[];
  linkedItemId: string | null;
} | null = null;

// ------------------------------------------------------------------ mount --

/**
 * Events the widget's own chrome must not leak into the host page.
 *
 * A shadow root hides the widget's INTERNALS from the page, not its events: an
 * event that crosses the boundary is re-targeted to the host element and keeps
 * bubbling, so `document` sees a genuine pointerdown "somewhere outside the
 * dialog". Every dismissable-layer implementation (Radix, Base UI, hand-rolled
 * ones) closes on exactly that — which is why clicking "Pick element" over an
 * open modal closed the modal, taking the element the tester meant to pick with
 * it. Stopping propagation AT the host leaves the widget's own handlers intact
 * (they run below it) and stops the page from ever hearing a click that was
 * never meant for it.
 *
 * Pick mode is unaffected: `pick.ts` listens on `window` in the CAPTURE phase,
 * which runs before the event reaches the host on its way down.
 */
const CONTAINED_EVENTS = [
  "pointerdown",
  "pointerup",
  "mousedown",
  "mouseup",
  "click",
  "dblclick",
  "auxclick",
  "touchstart",
  "touchend",
  "focusin",
  "focusout",
  "keydown",
  "keyup",
];

export function mountPanel(): ShadowRoot {
  const host = h("div", { id: "qa-deck-root", [CHROME_ATTR]: "" });
  document.documentElement.append(host);
  for (const type of CONTAINED_EVENTS) {
    host.addEventListener(type, (event) => event.stopPropagation());
  }
  root = host.attachShadow({ mode: "closed" });

  const style = document.createElement("style");
  style.textContent = PANEL_CSS;

  fabEl = h(
    "button",
    {
      class: "fab",
      type: "button",
      title: "QA deck (checklist + findings)",
      "data-ref": "QaDeck:Panel:fab",
      onclick: () => togglePanel(),
    },
    "QA",
  );

  routeEl = h("span", { class: "route", "data-ref": "QaDeck:Panel:route" });

  // Only meaningful once a session is running — without one the body already
  // IS the start form. Hidden rather than absent so the header never reflows.
  newSessionEl = h(
    "button",
    {
      class: "icon",
      type: "button",
      title: "Switch session, or start a new one",
      "data-ref": "QaDeck:Panel:new-session",
      onclick: () => openSessionPicker(),
    },
    "⇄",
  );

  const header = h(
    "header",
    {},
    h("span", { class: "title" }, "QA deck"),
    routeEl,
    h("span", { class: "spacer" }),
    newSessionEl,
    h(
      "button",
      {
        class: "icon",
        type: "button",
        title: "Close panel",
        "data-ref": "QaDeck:Panel:close",
        onclick: () => togglePanel(false),
      },
      "✕",
    ),
  );

  findingsTabEl = h(
    "button",
    {
      type: "button",
      "data-ref": "QaDeck:Panel:tab-findings",
      "data-tab": "findings",
      onclick: () => setTab("findings"),
    },
    "This page",
  );

  const tabs = h(
    "div",
    { class: "tabs" },
    h(
      "button",
      {
        type: "button",
        "data-ref": "QaDeck:Panel:tab-checklist",
        "data-tab": "checklist",
        onclick: () => setTab("checklist"),
      },
      "Checklist",
    ),
    findingsTabEl,
    (allTabEl = h(
      "button",
      {
        type: "button",
        "data-ref": "QaDeck:Panel:tab-all",
        "data-tab": "all",
        title: "Every finding in the project — change any status, any time",
        onclick: () => setTab("all"),
      },
      "All findings",
    )),
  );

  bodyEl = h("div", { class: "body", "data-ref": "QaDeck:Panel:body" });
  footEl = h("div", { class: "foot" });
  panelEl = h(
    "div",
    { class: "panel", "data-ref": "QaDeck:Panel:panel" },
    header,
    tabs,
    bodyEl,
    footEl,
  );

  // Boot straight back into the column the tester left. The route is settled
  // here rather than waiting for boot()'s `setRoute` so the restored panel's
  // first read is for the page actually on screen — and so that call then
  // no-ops instead of firing a second `refresh()` alongside this one.
  state.routeKey = currentRouteKey();
  state.open = api.getPanelOpen();

  // The finding in hand, if "Retest it" left one. Held across EVERY document
  // load, not just the one it was set for: a retest routinely reloads the page
  // (a form that redirects, a login bounce, the way back from a payment), and
  // spending the pin on arrival meant the card was gone exactly when the tester
  // had something to say about it. It is released by an act — the verdict,
  // "Close", or a finding that is no longer `fixed` — see `setFindingFocus`.
  //
  // The FIRST boot after "Retest it" is the arrival: panel open, Findings tab,
  // card in view. Every later one restores the card on the Findings tab (the
  // tab is not persisted, so a reload lands there or on the checklist — and the
  // retest is the job in hand) but leaves the open-state as the tester had it
  // and does not scroll: a page that reloads mid-scenario must not pop the
  // panel open each time.
  // A link from a tester digest wins over a held pin: it is what the tester
  // just clicked. It ARRIVES like "Retest it" — panel open, Findings tab, card
  // in view — and `openFinding` decides on fetch whether it becomes a held pin.
  const linked = api.takeLinkedFinding();
  const stored = linked ? null : api.getFindingFocus();
  if (linked) {
    pendingFocus = linked;
    state.tab = "findings";
    state.open = true;
  } else if (stored) {
    if (stored.fresh) {
      const held: api.FindingFocus = { ...stored, fresh: undefined };
      api.setFindingFocus(held);
      pendingFocus = held;
      state.tab = "findings";
      state.open = true;
    } else {
      restoredFocus = stored;
    }
  }

  // The same link clicked while the tester is ALREADY on its page changes only
  // the fragment, and a fragment change does not reload the document — so the
  // boot above never sees it. Same handling, on the event.
  window.addEventListener("hashchange", () => {
    const again = api.takeLinkedFinding();
    if (!again) return;
    togglePanel(true);
    void openFinding(again);
  });

  popupEl = h("div", { "data-ref": "QaDeck:Panel:popup-host" });
  root.append(style, fabEl, panelEl, popupEl);
  render();
  if (state.open) void refresh();
  startInboxPoll();
  return root;
}

/**
 * The one poll in the widget.
 *
 * Only `GET /api/qa/inbox`, only to move the badge and the strip — never a full
 * `refresh()`, which rebuilds the checklist and the finding form under whoever
 * is mid-sentence in them. It stops while the tab is hidden, because a tester
 * reading another page is not waiting on this, and a background tab polling a
 * customer's staging server every minute is rude.
 */
const INBOX_POLL_MS = 60_000;
let inboxTimer: ReturnType<typeof setInterval> | null = null;

function startInboxPoll(): void {
  const tick = () => {
    if (document.visibilityState !== "visible") return;
    void refreshInbox();
  };
  const arm = () => {
    if (inboxTimer) return;
    inboxTimer = setInterval(tick, INBOX_POLL_MS);
  };
  const disarm = () => {
    if (!inboxTimer) return;
    clearInterval(inboxTimer);
    inboxTimer = null;
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      arm();
      // Catch up straight away: a tester coming back to the tab should not wait
      // out the rest of an interval to learn a reply arrived while they were
      // somewhere else.
      tick();
    } else {
      disarm();
    }
  });
  if (document.visibilityState === "visible") arm();
}

function setTab(tab: State["tab"]): void {
  state.tab = tab;
  // A tab click is "show me the list again"; leaving the chat open underneath
  // would strand the tester on a conversation they thought they had left.
  state.chat = null;
  // A popup belongs to the list it was opened from.
  state.all.popup = null;
  render();
  // Fresh on every visit: the list is the project's, and anyone may have
  // moved anything in it since the last look.
  if (tab === "all") void loadAll();
}

/** Push the page content left while the panel is open (instead of covering
 *  it): a margin on <html> reflows and re-centers everything in normal flow.
 *  Accepted limit: `position: fixed` host elements don't shift — they sit
 *  under the panel until it's closed. Skipped on narrow viewports where the
 *  panel is full-width anyway. */
const PANEL_WIDTH_PX = 380;
let pagePushSaved: { marginRight: string; transition: string } | null = null;

function applyPagePush(open: boolean): void {
  const el = document.documentElement;
  if (open && window.innerWidth > PANEL_WIDTH_PX * 2) {
    if (!pagePushSaved) {
      pagePushSaved = {
        marginRight: el.style.marginRight,
        transition: el.style.transition,
      };
    }
    el.style.transition = "margin-right 150ms ease";
    el.style.marginRight = `${PANEL_WIDTH_PX}px`;
  } else if (pagePushSaved) {
    el.style.marginRight = pagePushSaved.marginRight;
    el.style.transition = pagePushSaved.transition;
    pagePushSaved = null;
  }
}

export function togglePanel(open?: boolean): void {
  state.open = open ?? !state.open;
  if (!state.open && isPicking()) stopPick();
  // Remembered across a document load: a multi-page scenario otherwise closes
  // the panel out from under the tester halfway through its own steps.
  api.setPanelOpen(state.open);
  render();
  if (state.open) void refresh();
}

// ----------------------------------------------------------------- loading --

export function setRoute(routeKey: string): void {
  if (routeKey === state.routeKey) return;
  state.routeKey = routeKey;
  state.expanded.clear();
  // `state.focus` deliberately survives this: the obvious next move after
  // opening a finding is to go and look at the page it names, and dropping the
  // card on the way there would take the answer buttons with it. "Close" is
  // how it goes away — and a retest pin survives a full document load too, by
  // way of storage (`api.setFindingFocus`).
  //
  // The scenario a CHAT is about, by contrast, belongs to the surface just left.
  state.chat = null;
  render();
  if (state.open) void refresh();
}

export async function refresh(): Promise<void> {
  if (!api.getToken()) {
    render();
    return;
  }
  state.loading = true;
  state.error = null;
  render();

  try {
    if (!state.config) {
      state.config = await api.getConfig();
      // The project's locales and default sub-views only arrive here, so the
      // route key computed at boot may have been wrong. Recompute before the
      // checklist read that depends on it.
      routeConfig = state.config.routeConfig;
      state.routeKey = currentRouteKey();
    }
    if (!state.session) state.session = api.getStoredSession();

    const [checklists, findings, retest, inbox] = await Promise.all([
      api.getChecklists(state.routeKey),
      api.listFindings(state.routeKey),
      // Never fatal: the retest queue is a convenience, and losing it must not
      // cost the tester the checklist they came for.
      state.retestLoaded
        ? Promise.resolve(null)
        : api.getRetestQueue().catch(() => ({ items: [] })),
      // Same rule, one step further: a server that predates this endpoint 404s,
      // and the answer to that is no strip and no badge — not an error the
      // tester has to read past to reach the checklist.
      api.getInbox(state.inboxShowDismissed).catch(() => null),
    ]);
    state.items = checklists.items;
    state.otherViewItems = checklists.other_views;
    state.globalItems = checklists.global;
    state.findings = findings.findings;
    if (inbox) {
      state.inbox = inbox;
      // Seed the poll's comparison too, or its first tick reads "changed" and
      // re-renders the body for news the full refresh already showed.
      inboxSignature = signatureOf(inbox);
    }
    if (retest) {
      state.retest = new Set(retest.items.map((i) => i.item_id));
      state.retestTotal = retest.items.length;
      state.retestLoaded = true;
    }

    if (state.session) {
      // A stored session can outlive the server's copy — the tester's tenant
      // was re-seeded, or the session was pruned. Recover HERE rather than in
      // the outer catch, so only this call's 404 clears the session and an
      // unrelated 404 elsewhere in the load cannot log the tester out of one.
      try {
        const { items } = await api.getChecklistState(state.session.id);
        state.itemState = new Map(items.map((i) => [i.item_id, i]));
      } catch (error) {
        if (error instanceof api.ApiClientError && error.status === 404) {
          api.setStoredSession(null);
          state.session = null;
          state.itemState = new Map();
          state.error =
            "That QA session no longer exists — start a new one to keep recording.";
        } else {
          throw error;
        }
      }
    }
    state.outbox = await api.flushOutbox();
    // Learned once and cached: the panel needs it to say whether a claimed fix
    // is on the environment in front of the tester, long before anyone files
    // anything. A consumer with no probe leaves it null and nothing changes.
    //
    // Re-render when it lands rather than awaiting it. Awaiting would hold the
    // whole panel behind a probe on someone else's server; NOT re-rendering at
    // all was the bug — the first paint happens before the fetch resolves, so
    // every retest row rendered the degraded note ("Fixed in <sha>.") instead
    // of "— the build you are on", which is the entire point of the stamp. Seen
    // on staging with the sha matching exactly and the row still hedging.
    // `renderKeepingFocus` so a tester mid-sentence does not lose their caret.
    void ensureBuild().then(() => {
      if (getBuild()) renderKeepingFocus();
    });
  } catch (error) {
    if (error instanceof api.ApiClientError && error.status === 401) {
      api.setToken(null);
      state.config = null;
      state.error = "Token rejected — enter a valid tester token.";
    } else if (error instanceof api.ApiClientError && error.status === 403) {
      state.error =
        "This origin is not on the project's allowlist. Add it in the qadeck dashboard.";
    } else {
      state.error = error instanceof Error ? error.message : "Request failed";
    }
    state.outbox = api.outboxSize();
  } finally {
    state.loading = false;
    render();
    // After the render that built the list, not before it: there is nothing to
    // scroll to until the card exists. Once only — a pointer applied on every
    // refresh would yank the tester back every time the inbox polled.
    if (pendingFocus) {
      const arriving = pendingFocus;
      pendingFocus = null;
      void openFinding(arriving);
    } else if (restoredFocus) {
      const held = restoredFocus;
      restoredFocus = null;
      void openFinding(held, { quiet: true });
    }
  }
}

/**
 * Re-read the inbox alone.
 *
 * The body is only rebuilt when the answer actually CHANGED. A poll that
 * re-renders every minute regardless would move the caret of a tester
 * mid-sentence once a minute for no news at all — `renderKeepingFocus` softens
 * that, it does not make it free.
 */
let inboxSignature = "";

async function refreshInbox(): Promise<void> {
  if (!api.getToken()) return;
  let next: api.TesterInbox | null;
  try {
    next = await api.getInbox(state.inboxShowDismissed);
  } catch {
    // Never fatal, and never destructive: a poll that failed leaves whatever
    // the last successful read said rather than blanking the strip.
    return;
  }

  state.inbox = next;
  const signature = signatureOf(next);
  if (signature === inboxSignature) {
    // Nothing new. The badge is still repainted in case a thread was read in
    // another tab, and it costs one attribute write.
    renderFab();
    return;
  }
  inboxSignature = signature;
  renderKeepingFocus();
}

function signatureOf(inbox: api.TesterInbox | null): string {
  if (!inbox) return "";
  const replies = wireList(inbox.replies)
    .map((r) => `${r.finding_id}:${r.unread}:${r.latest?.created_at ?? ""}`)
    .join("|");
  const retests = wireList(inbox.retests)
    .map((r) => `${r.finding_id}:${r.fixed_at}`)
    .join("|");
  // Both halves of the dismissed list: a signature blind to the snoozed
  // questions left the list on "Loading…" when that was all it held.
  const dismissed = [
    ...wireList(inbox.dismissed),
    ...wireList(inbox.dismissed_discussions),
  ]
    .map((r) => r.finding_id)
    .join("|");
  const discussions = wireList(inbox.discussions)
    .map((r) => `${r.finding_id}:${r.asked_at}:${r.reminder ? "r" : ""}`)
    .join("|");
  const answers = wireList(inbox.answers)
    .map((r) => `${r.finding_id}:${r.latest.created_at}`)
    .join("|");
  return [
    replies,
    retests,
    discussions,
    answers,
    inbox.checklist?.changed ? "c" : "",
    inbox.dismissed_count ?? 0,
    dismissed,
  ].join("#");
}

/**
 * How many FINDINGS are waiting on the tester: the edge tab, the All findings
 * tab and the "Needs me" chip all show this one number.
 *
 * Findings, not messages — "3 need you" is three things to do, and a finding
 * with a question, a reply and a fix claim on it is still one. Answers are in
 * it because a READ question nobody answered is still waiting; replies are in
 * it because an unread one is news on a finding answers do not cover (a closed
 * one).
 */
function inboxCount(): number {
  const inbox = state.inbox;
  if (!inbox) return 0;
  return new Set([
    ...wireList(inbox.replies).map((row) => row.finding_id),
    ...wireList(inbox.answers).map((row) => row.finding_id),
    ...wireList(inbox.retests).map((row) => row.finding_id),
    ...wireList(inbox.discussions).map((row) => row.finding_id),
  ]).size;
}

/** Unread replies on findings the CURRENT route is showing. */
function unreadHere(): number {
  const inbox = state.inbox;
  if (!inbox) return 0;
  const onPage = new Set(state.findings.map((f) => f.id));
  return wireList(inbox.replies)
    .filter((row) => onPage.has(row.finding_id))
    .reduce((total, row) => total + (row.unread || 0), 0);
}

/**
 * Drop one finding's row from the strip, without waiting for the next poll.
 *
 * The server already stamped the read mark when the thread was fetched; this
 * keeps the strip honest in the meantime, so opening a conversation visibly
 * clears the thing that pointed at it.
 */
function clearInboxReply(findingId: string): void {
  const inbox = state.inbox;
  if (!inbox) return;
  inbox.replies = wireList(inbox.replies).filter(
    (row) => row.finding_id !== findingId,
  );
  inboxSignature = signatureOf(inbox);
}

/**
 * Drop one finding from the answers list, without waiting for the next poll.
 *
 * Called on an ACT only — a reply, a verdict, a status change — never on a
 * read: reading a question is exactly what used to make it vanish unanswered.
 */
function clearInboxAnswer(findingId: string): void {
  const inbox = state.inbox;
  if (!inbox) return;
  inbox.answers = wireList(inbox.answers).filter(
    (row) => row.finding_id !== findingId,
  );
  inboxSignature = signatureOf(inbox);
}

/**
 * Drop one fix claim from the strip, without waiting for the next poll.
 *
 * The tester just answered it; leaving the row up says the deck did not hear
 * them. The server stops reporting it on its own once the status moves — this
 * only closes the gap until the next read.
 */
function clearInboxRetest(findingId: string): void {
  const inbox = state.inbox;
  if (!inbox) return;
  inbox.retests = wireList(inbox.retests).filter(
    (row) => row.finding_id !== findingId,
  );
  inboxSignature = signatureOf(inbox);
}

/** Drop one answered question from the strip, ahead of the next poll. */
function clearInboxDiscussion(findingId: string): void {
  const inbox = state.inbox;
  if (!inbox) return;
  inbox.discussions = wireList(inbox.discussions).filter(
    (row) => row.finding_id !== findingId,
  );
  inboxSignature = signatureOf(inbox);
}

/**
 * "I have seen this."
 *
 * Dismisses the fix claims the strip is HOLDING — named by id, so a claim the
 * strip never showed (it is capped at a page) or one that lands later is not
 * swept out with them — and stamps the tester's inbox clock, which is what the
 * checklist-changed row is measured against. Unread REPLIES survive it on
 * purpose — those are cleared by reading the thread, and a dismiss that
 * silently marked conversations read would lose the one thing nobody else can
 * answer for the tester.
 */
async function dismissInbox(): Promise<void> {
  const shown = wireList(state.inbox?.retests).map((row) => row.finding_id);
  // Questions on the strip are snoozed by the same press, not dismissed: the
  // server brings each back after a day until it is answered.
  const asked = wireList(state.inbox?.discussions).map((row) => row.finding_id);
  state.inboxOpen = false;
  render();
  try {
    await api.markInboxSeen(shown, asked);
    await refreshInbox();
  } catch {
    /* the strip is a convenience; a failed dismiss simply comes back */
  }
}

/**
 * The undo for ✕, one claim at a time.
 *
 * Released by an ACT, the way the pinned card is: a dismissal is a stamp on
 * the server, the dismissed list is fetched fresh, and bringing a claim back
 * clears the stamp — so a mis-click survives a page load and a night's
 * sleep, and nothing here runs on a clock.
 */
async function restoreInboxRetest(findingId: string): Promise<void> {
  try {
    await api.restoreInboxRetests([findingId]);
  } catch {
    /* the row stays where it is, and the tester can press it again */
    return;
  }
  await refreshInbox();
}

/** Open or close the dismissed list; opening fetches it. */
function toggleDismissed(): void {
  state.inboxShowDismissed = !state.inboxShowDismissed;
  state.inboxOpen = true;
  render();
  void refreshInbox();
}

// ------------------------------------------------------------------ render --

function render(): void {
  if (!root) return;
  applyPagePush(state.open);
  renderFab();
  panelEl.setAttribute("data-open", String(state.open));
  routeEl.textContent = state.routeKey;
  // The count on the tab is what finds a reply without the strip: a thread
  // lives under a finding card, and the Findings tab is where that card is.
  const here = unreadHere();
  findingsTabEl.textContent = here ? `This page (${here})` : "This page";
  // The same number as the edge tab and the "Needs me" chip: one count of
  // what is waiting on the tester, wherever they look for it.
  const waiting = inboxCount();
  clear(allTabEl);
  allTabEl.append("All findings");
  if (waiting) {
    allTabEl.append(
      h(
        "span",
        { class: "tab-badge", "data-ref": "QaDeck:Panel:tab-all-count" },
        String(waiting),
      ),
    );
  }
  for (const tab of Array.from(root.querySelectorAll(".tabs button"))) {
    tab.setAttribute(
      "data-active",
      String(tab.getAttribute("data-tab") === state.tab),
    );
  }

  newSessionEl.style.display = state.session ? "" : "none";

  clear(bodyEl);
  if (state.error) bodyEl.append(h("div", { class: "error" }, state.error));

  if (!api.getToken()) {
    bodyEl.append(renderTokenForm());
  } else if (!state.session || state.sessionPicker) {
    bodyEl.append(renderSessionStart());
  } else if (state.chat) {
    bodyEl.append(renderItemChat(state.chat));
  } else if (state.tab === "checklist") {
    bodyEl.append(renderChecklist());
  } else if (state.tab === "all") {
    bodyEl.append(renderAll());
  } else {
    bodyEl.append(renderFindings());
  }

  clear(popupEl);
  const popup = state.all.popup;
  if (
    popup &&
    state.open &&
    state.tab === "all" &&
    state.all.filter === "needs_me" &&
    !state.chat
  ) {
    const { groups } = needsMeGroups(state.all.findings);
    popupEl.append(
      renderTurnPopup(
        popup,
        TURNS.find((t) => t.kind === popup.kind)!.title,
        groups.get(popup.kind)!,
      ),
    );
  }

  renderFoot();
}

/**
 * The tab on the page edge, with a count while the panel is closed.
 *
 * Zero news, no badge — and no badge at all against a server with no inbox
 * endpoint, because a silent absence is the honest degradation and a "0" is a
 * claim we cannot make.
 */
function renderFab(): void {
  if (!fabEl) return;
  fabEl.setAttribute("data-open", String(state.open));
  const count = state.open ? 0 : inboxCount();
  clear(fabEl);
  fabEl.append("QA");
  if (count) {
    fabEl.append(
      h(
        "span",
        { class: "fab-badge", "data-ref": "QaDeck:Panel:fab-badge" },
        String(count),
      ),
    );
    fabEl.title = `QA deck — ${count} new for you`;
  } else {
    fabEl.title = "QA deck (checklist + findings)";
  }
}

/** The status bar alone, so transient progress ("attaching image…") can be
 *  shown without rebuilding the body — which would throw away the caret of
 *  whoever is mid-sentence in the box that started it. */
function renderFoot(): void {
  clear(footEl);
  footEl.append(
    h(
      "span",
      {},
      state.loading
        ? "syncing…"
        : state.outbox
          ? `${state.outbox} queued offline`
          : "synced",
    ),
    h("span", { class: "spacer", style: "flex:1" }),
    state.session
      ? h(
          "span",
          { class: "note" },
          `${state.session.tester}${state.session.name ? ` · ${state.session.name}` : ""}`,
        )
      : h(
          "span",
          { class: "note" },
          state.config ? state.config.project.name : "no session",
        ),
  );
  if (state.status) footEl.prepend(h("span", { class: "ok" }, state.status));
}

/** How long a transient confirmation stays on the status line. */
const STATUS_MS = 3_000;
let statusTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Say that something was recorded, then get out of the way.
 *
 * The panel's answer to a toast: the status line is already the place the
 * tester looks for "saved" / "queued offline", and a real toast over someone
 * else's page would be one more floating layer in a product whose whole job is
 * not to disturb the page under it.
 */
function flashStatus(message: string): void {
  state.status = message;
  if (statusTimer) clearTimeout(statusTimer);
  statusTimer = setTimeout(() => {
    statusTimer = null;
    state.status = null;
    renderFoot();
  }, STATUS_MS);
  renderFoot();
}

/** A half-second tint on the row that was just marked, so the eye can follow a
 *  verdict given far down a long list. Purely decorative — guarded so a browser
 *  without the Web Animations API simply doesn't flash. */
function flashItem(itemId: string): void {
  const row = root.querySelector<HTMLElement>(
    `[data-item-id="${CSS.escape(itemId)}"]`,
  );
  row?.animate?.(
    [{ background: "rgba(124,92,255,.22)" }, { background: "transparent" }],
    { duration: 500, easing: "ease-out" },
  );
}

/**
 * Re-render and put the tester back where they were.
 *
 * `render()` rebuilds the whole body, which is fine when a click caused it and
 * hostile when a paste did: attaching a screenshot mid-sentence would blur the
 * box, scroll the panel to the top and lose the caret. Elements are matched by
 * `data-ref`, which every interactive one already carries for the e2e specs.
 */
function renderKeepingFocus(): void {
  const active = root.activeElement as HTMLElement | null;
  const ref = active?.getAttribute("data-ref") ?? null;
  const caret =
    active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement
      ? active.selectionStart
      : null;
  const scroll = bodyEl.scrollTop;

  render();

  bodyEl.scrollTop = scroll;
  if (!ref) return;
  const restored = root.querySelector<HTMLElement>(
    `[data-ref="${CSS.escape(ref)}"]`,
  );
  if (!restored) return;
  restored.focus();
  if (
    caret !== null &&
    (restored instanceof HTMLTextAreaElement ||
      restored instanceof HTMLInputElement)
  ) {
    try {
      restored.setSelectionRange(caret, caret);
    } catch {
      /* input types that don't support selection (e.g. email) */
    }
  }
}

function renderTokenForm(): Node {
  const input = h("input", {
    type: "password",
    placeholder: "tester token",
    "data-ref": "QaDeck:Auth:token-input",
  });
  const save = async () => {
    const value = input.value.trim();
    if (!value) return;
    api.setToken(value);
    state.error = null;
    await refresh();
  };
  return h(
    "div",
    {},
    h(
      "p",
      { class: "note" },
      "This panel is for manual QA. Enter the tester token you were given.",
    ),
    h("label", {}, "Tester token"),
    input,
    h(
      "div",
      { style: "margin-top:8px" },
      h(
        "button",
        {
          class: "btn primary",
          type: "button",
          "data-ref": "QaDeck:Auth:save-token",
          onclick: save,
        },
        "Connect",
      ),
    ),
  );
}

/**
 * Send the tester back to where the session left off.
 *
 * Same-origin ONLY, and that is a security rule rather than a nicety: the URL
 * comes off the wire, so navigating to it unchecked would turn "resume" into an
 * open redirect on the customer's own page. A stored URL from another origin —
 * or one that fails to parse — is ignored, and the tester simply stays put.
 *
 * Returns true when a navigation was started, so the caller can stop: the page
 * is about to be replaced and any further work on it is wasted.
 */
function resumeTo(session: QaSession): boolean {
  if (!session.last_url) return false;
  let target: URL;
  try {
    target = new URL(session.last_url, location.href);
  } catch {
    return false;
  }
  if (target.origin !== location.origin) return false;
  if (target.href === location.href) return false;
  location.assign(target.href);
  return true;
}

/**
 * Return to the start form WITHOUT ending the old session on the server: it
 * stays open and appears in "Resume a session", so a tester who started a new
 * one by mistake has not thrown away what they already recorded.
 */
/**
 * Open the session chooser WITHOUT dropping the current session.
 *
 * The first version cleared it up front, which made "switch" impossible: the
 * only way to reach the list was to stop being in a session, and backing out
 * left the tester in none at all. The current session is now held until another
 * one is actually chosen.
 */
function openSessionPicker(): void {
  // Re-read on open: "12 recorded · last 2 min ago" is the whole point, and it
  // ages the moment the tester keeps working.
  invalidateSessionList();
  state.sessionPicker = true;
  state.error = null;
  state.status = null;
  render();
}

function closeSessionPicker(): void {
  state.sessionPicker = false;
  render();
}

/** Adopt `session` as the active one — used by both switch and start.
 *  Not `useSession`: the `use` prefix makes lint read it as a React hook. */
async function activateSession(session: QaSession): Promise<void> {
  const switching = session.id !== state.session?.id;
  api.setStoredSession(session);
  // A new session, or a different one marked "current" — either way the cached
  // list no longer describes what the picker should show.
  invalidateSessionList();
  state.session = session;
  state.sessionPicker = false;
  if (switching) {
    // Another session's verdicts are not this one's; showing the old marks
    // against the new session would be a lie until the refresh lands.
    state.itemState = new Map();
  }
  if (resumeTo(session)) return; // navigating away; the reload will refresh
  await refresh();
}

/**
 * The tester's sessions, held in state rather than re-fetched per render.
 *
 * It used to be a fire-and-forget fetch that filled a div after the fact, so
 * every re-render blanked the list until the next response landed — a flicker
 * while reading it, and a button that could vanish under a click. Same shape as
 * `issueTargets`: fetched once, invalidated when something makes it untrue.
 */
let sessionList: {
  sessions: QaSession[];
  recorded: Record<string, number>;
  lastRecordedAt: Record<string, string>;
} | null = null;
let sessionListLoading = false;

function ensureSessionList(): void {
  if (sessionList || sessionListLoading) return;
  sessionListLoading = true;
  void api
    .listSessions()
    .then(({ sessions, recorded, lastRecordedAt }) => {
      sessionList = {
        sessions,
        recorded: recorded ?? {},
        lastRecordedAt: lastRecordedAt ?? {},
      };
    })
    .catch(() => {
      // A list we could not load must not block starting a sweep; an empty one
      // renders as "no sessions to resume", which is the honest fallback.
      sessionList = { sessions: [], recorded: {}, lastRecordedAt: {} };
    })
    .finally(() => {
      sessionListLoading = false;
      render();
    });
}

/** Something happened that makes the cached list wrong (a session started,
 *  switched, or verdicts recorded against it). */
function invalidateSessionList(): void {
  sessionList = null;
}

/** "3 minutes ago" — enough precision to tell a live sweep from a stale one. */
function sinceLabel(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso.slice(0, 10);
  const minutes = Math.round((Date.now() - then) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function renderSessionStart(): Node {
  const active = state.session;
  const nameInput = h("input", {
    type: "text",
    placeholder: "e.g. staging sweep 2026-08-10",
    "data-ref": "QaDeck:Session:name-input",
  });

  const start = async () => {
    const name = nameInput.value.trim() || null;
    // Caught here as well as server-side so the tester is told BEFORE a round
    // trip, and told what to do about it. See `shared/token-shape.ts`.
    if (looksLikeTesterToken(name)) {
      state.error =
        "That is your tester token, not a session name — a name is shown to " +
        "everyone reading the findings. The token belongs in the token field.";
      render();
      return;
    }
    try {
      await activateSession(await api.createSession(name));
    } catch (error) {
      state.error =
        error instanceof Error ? error.message : "Could not start session";
      render();
    }
  };

  /**
   * Resumable sessions, ABOVE the start form when there is no active one.
   *
   * A sweep lives on the server; only the pointer to it lives in this browser.
   * So a tester opening the panel on a second computer — or after clearing site
   * data — used to be shown an empty "start a session" box with their existing
   * work listed underneath as a bare name and date, which reads as nothing to
   * do with them. Their verdicts looked lost. Leading with the sessions, and
   * saying how much each one holds, is the whole fix.
   *
   * Still an explicit click, never automatic: one token can be shared by a
   * team, and silently adopting a session someone else has open would interleave
   * two people's ledgers. The "last recorded" stamp is what tells them apart.
   */
  const listWrap = h("div", {});
  ensureSessionList();
  const open =
    sessionList?.sessions.filter((s) => !s.ended_at).slice(0, 8) ?? [];
  if (open.length) {
    listWrap.append(
      h("label", {}, active ? "Switch to a session" : "Resume your sweep"),
    );
    if (!active) {
      listWrap.append(
        h(
          "p",
          // NOT `resume-note`: `resume-<id>` is the button namespace, and a
          // prefix selector (every spec uses one) would match this paragraph
          // first and click a <p>.
          { class: "note", "data-ref": "QaDeck:Session:cross-device" },
          "Your sweep is stored on the server, not in this browser — pick it up here, on any computer.",
        ),
      );
    }
    for (const session of open) {
      const current = session.id === active?.id;
      const count = sessionList?.recorded[session.id] ?? 0;
      const lastAt = sessionList?.lastRecordedAt[session.id];
      const parts = [
        session.name ?? "unnamed",
        count
          ? `${count} recorded${lastAt ? ` · last ${sinceLabel(lastAt)}` : ""}`
          : `started ${session.started_at.slice(0, 10)} · nothing recorded yet`,
        session.last_route_key ?? null,
        current ? "current" : null,
      ].filter(Boolean);
      listWrap.append(
        h(
          "div",
          { style: "margin-bottom:4px" },
          h(
            "button",
            {
              class: `btn${current ? " active" : ""}`,
              type: "button",
              // NOT disabled: clicking the session you are already in is the
              // obvious way to say "never mind, go back to it", and a
              // disabled button silently swallows that click.
              "data-ref": `QaDeck:Session:resume-${session.id}`,
              onclick: async () => {
                if (current) return closeSessionPicker();
                await activateSession(session);
              },
            },
            parts.join(" · "),
          ),
        ),
      );
    }
  } else if (!sessionList) {
    listWrap.append(h("p", { class: "note" }, "Looking for earlier sweeps…"));
  }

  const wrap = h(
    "div",
    {},
    h(
      "p",
      { class: "note" },
      active
        ? "Switch to another open session, or start a new one. Checklist state and findings are recorded against whichever is active."
        : "Pick up a sweep you already started, or begin a new one. Checklist state and findings are recorded against whichever is active.",
    ),
    listWrap,
    h("label", {}, "Or start a new sweep — name (optional)"),
    nameInput,
    h(
      "div",
      { style: "margin-top:8px" },
      h(
        "button",
        {
          class: "btn primary",
          type: "button",
          "data-ref": "QaDeck:Session:start",
          onclick: start,
        },
        "Start session",
      ),
    ),
  );

  // Backing out has to be possible, or opening the chooser by mistake would
  // strand the tester away from the checklist they were working through.
  if (active) {
    wrap.append(
      h(
        "div",
        { style: "margin-top:14px" },
        h(
          "button",
          {
            class: "btn",
            type: "button",
            "data-ref": "QaDeck:Session:cancel",
            onclick: () => closeSessionPicker(),
          },
          `Keep “${active.name ?? "unnamed"}”`,
        ),
      ),
    );
  }

  return wrap;
}

// ------------------------------------------------------------- attachments --

/** Matches the server's per-note ceiling (`CreateFindingBody.attachment_ids`). */
const MAX_IMAGES = 10;

/**
 * One thumbnail. The bytes need the bearer token, so an `<img src>` pointing at
 * the API would simply 401 — the URL here is always an object URL, filled in
 * from the cache or as soon as the fetch lands.
 */
function imageThumb(
  id: string,
  refPrefix: string,
  onRemove: (() => void) | null,
): Node {
  const img = h("img", {
    alt: "attached screenshot",
    "data-ref": `${refPrefix}-image-${id}`,
  }) as HTMLImageElement;
  const cached = attachmentUrl(id);
  if (cached) img.src = cached;
  else {
    void loadAttachment(id).then((url) => {
      if (url) img.src = url;
    });
  }
  img.addEventListener("click", () => {
    const url = attachmentUrl(id);
    // The host page is someone else's, so a lightbox inside the panel would be
    // 380px wide — a new tab is the only place a screenshot is readable.
    if (url) window.open(url, "_blank", "noopener");
  });

  const wrap = h("div", { class: "thumb" }, img);
  if (onRemove) {
    wrap.append(
      h(
        "button",
        {
          class: "thumb-x",
          type: "button",
          title: "Remove this image",
          "data-ref": `${refPrefix}-image-remove-${id}`,
          onclick: onRemove,
        },
        "✕",
      ),
    );
  }
  return wrap;
}

/**
 * Upload, then hand the caller the new id list.
 *
 * Each image is uploaded on its own so one failure costs one screenshot rather
 * than the batch, and the ones that worked are still attached.
 */
async function attachImages(
  files: File[],
  current: string[],
  onChange: (ids: string[]) => void,
): Promise<void> {
  if (!files.length) return;
  if (!state.session) {
    state.error = "Start a session before attaching an image.";
    render();
    return;
  }
  const sessionId = state.session.id;

  const room = MAX_IMAGES - current.length;
  if (room <= 0) {
    state.error = `At most ${MAX_IMAGES} images per note.`;
    render();
    return;
  }
  const accepted = files.slice(0, room);

  state.error =
    accepted.length < files.length
      ? `Only ${room} more image(s) fit on this note.`
      : null;
  state.status =
    accepted.length > 1 ? `attaching ${accepted.length} images…` : "attaching…";
  renderFoot();

  const next = [...current];
  for (const file of accepted) {
    try {
      next.push(await uploadImage(file, sessionId));
    } catch (error) {
      // The words are the finding; a screenshot that would not upload is worth
      // saying out loud and not worth losing them over.
      state.error = `Image not attached: ${
        error instanceof Error ? error.message : "upload failed"
      }`;
    }
  }
  state.status = null;
  onChange(next);
}

/** The "Add image" control plus the thumbnails already attached. */
function renderAttachments(options: {
  refPrefix: string;
  ids: string[];
  onChange: (ids: string[]) => void;
}): Node {
  const { refPrefix, ids, onChange } = options;
  const row = h("div", { class: "thumbs" });
  for (const id of ids) {
    row.append(
      imageThumb(id, refPrefix, () =>
        onChange(ids.filter((other) => other !== id)),
      ),
    );
  }

  const input = h("input", {
    type: "file",
    accept: "image/*",
    multiple: true,
    style: "display:none",
    "data-ref": `${refPrefix}-image-input`,
  }) as HTMLInputElement;
  input.addEventListener("change", () => {
    const files = Array.from(input.files ?? []);
    // Cleared before the await, or picking the same file twice in a row fires
    // no `change` the second time.
    input.value = "";
    void attachImages(files, ids, onChange);
  });

  row.append(
    h(
      "button",
      {
        class: "btn",
        type: "button",
        "data-ref": `${refPrefix}-image-add`,
        onclick: () => input.click(),
      },
      ids.length ? "Add image" : "Attach image",
    ),
    input,
  );
  return row;
}

/**
 * Paste-to-attach on a text box.
 *
 * How a screenshot actually reaches a bug report: grab region, click in the
 * box, paste. Making the tester save the file first and then find it in a
 * picker is the friction that stops people attaching anything at all.
 */
function enableImagePaste(
  field: HTMLElement,
  ids: () => string[],
  onChange: (ids: string[]) => void,
): void {
  field.addEventListener("paste", (event) => {
    const items = (event as ClipboardEvent).clipboardData?.items;
    if (!items) return;
    const files = Array.from(items)
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((file): file is File => Boolean(file));
    if (!files.length) return; // a normal text paste — leave it alone
    event.preventDefault();
    void attachImages(files, ids(), onChange);
  });
}

// --------------------------------------------------------------- checklist --

/** Group by the spec's human-readable `###` heading (with any leading
 *  "N. " ordinal stripped) — NOT by filename: testers shouldn't have to decode
 *  `ai-kickstart-canvas-drafts.md`. Keyed by specFile+group so two specs
 *  reusing a heading never merge.
 *
 *  The plain-language rewrite wins when the ingest produced one; the spec's own
 *  heading is the fallback, so a project with no AI titles reads exactly as it
 *  did before the feature existed. */
function groupLabel(item: ChecklistItem): string {
  return (
    item.qaGroup?.trim() ||
    item.group.replace(/^\d+\.\s*/, "").trim() ||
    item.specFile.replace(/\.md$/, "")
  );
}

/** Same fallback chain for a scenario row. */
function itemLabel(item: ChecklistItem): string {
  return item.qaTitle?.trim() || item.title;
}

function groupBySpec(
  items: ChecklistItem[],
): Map<string, { label: string; items: ChecklistItem[] }> {
  const groups = new Map<string, { label: string; items: ChecklistItem[] }>();
  for (const item of items) {
    const key = `${item.specFile}#${item.group}`;
    const entry = groups.get(key) ?? { label: groupLabel(item), items: [] };
    entry.items.push(item);
    groups.set(key, entry);
  }
  return groups;
}

/** Items in the retest queue, out of a bucket the current route produced. */
function retestOf(items: ChecklistItem[]): ChecklistItem[] {
  return items.filter((item) => state.retest.has(item.id));
}

const INBOX_REF = "QaDeck:Inbox";

/**
 * "New for you" — the strip above the checklist.
 *
 * The tester's problem is not reading a thread, it is knowing there is one:
 * the panel shows the findings on the page they are standing on, so a reply on
 * a page they have left is invisible. Three kinds of news, one strip, because a
 * tester who has to look in three places looks in none.
 *
 * Returns null when there is nothing to say — an empty strip is a permanent
 * "nothing happened" banner, which is worse than no strip at all.
 */
/**
 * One item of news, whatever kinds of news it is.
 *
 * Merged by FINDING, because the two feeds are about the same objects: a reply
 * and a fix claim on one finding used to be two rows with two identical
 * buttons — the same finding listed twice, two controls carrying the same
 * `data-ref`, and nothing saying they were the same thing. One row, one
 * destination, badges for what is new.
 */
interface InboxItem {
  findingId: string;
  url: string;
  /** Unread replies, if any — the excerpt and the count come from the newest. */
  reply: api.TesterInbox["replies"][number] | null;
  /** A fix claimed on this finding, if any. */
  retest: api.TesterInbox["retests"][number] | null;
  /** A question waiting for this tester's view, if any. Never alongside a
   *  retest: asking clears the claim. */
  discussion: NonNullable<api.TesterInbox["discussions"]>[number] | null;
}

function inboxItems(inbox: api.TesterInbox): InboxItem[] {
  const byFinding = new Map<string, InboxItem>();
  const item = (id: string, url: string): InboxItem => {
    const existing = byFinding.get(id);
    if (existing) return existing;
    const fresh: InboxItem = {
      findingId: id,
      url,
      reply: null,
      retest: null,
      discussion: null,
    };
    byFinding.set(id, fresh);
    return fresh;
  };
  // Retests first: a claim waiting on a verdict is the more actionable of the
  // two, and insertion order is the order the rows render in.
  for (const retest of wireList(inbox.retests)) {
    item(retest.finding_id, retest.url).retest = retest;
  }
  // Then questions: only this tester can answer them either, but nothing is
  // broken while they wait.
  for (const discussion of wireList(inbox.discussions)) {
    item(discussion.finding_id, discussion.url).discussion = discussion;
  }
  for (const reply of wireList(inbox.replies)) {
    item(reply.finding_id, reply.url).reply = reply;
  }
  return Array.from(byFinding.values());
}

/** Rows the strip shows before "Show all" — it is a notification list
 *  above the work, and must never push the work off the screen. */
const INBOX_PREVIEW = 3;

/**
 * `rowsHere: false` is the "Needs me" view, which lists every one of these
 * jobs right underneath: the strip keeps its counts, ✕ and the way back to
 * what was dismissed, and leaves the rows to the list.
 */
function renderInbox({ rowsHere = true } = {}): Node | null {
  const inbox = state.inbox;
  if (!inbox) return null;
  const items = inboxItems(inbox);
  const checklistChanged = Boolean(inbox.checklist?.changed);
  const dismissedCount = inbox.dismissed_count ?? 0;
  const live = items.length > 0 || checklistChanged;
  if (!live && !dismissedCount) return null;

  const retests = items.filter((i) => i.retest).length;
  const questions = items.filter((i) => i.discussion).length;
  // Unread words on a question ARE the question — see `inboxCount`.
  const replies = items.reduce(
    (n, i) => n + (i.discussion ? 0 : (i.reply?.unread ?? 0)),
    0,
  );

  // The way back to a dismissed claim. Offered whenever there is one, so a
  // mis-click on ✕ is undoable from the very strip it emptied — otherwise the
  // strip vanishes with the last row and takes the undo with it.
  const dismissedButton = dismissedCount
    ? h(
        "button",
        {
          class: `inbox-dismissed${state.inboxShowDismissed ? " active" : ""}`,
          type: "button",
          title: state.inboxShowDismissed
            ? "Hide what you dismissed"
            : "Show what you dismissed, and bring any of it back",
          "data-ref": `${INBOX_REF}:dismissed`,
          onclick: toggleDismissed,
        },
        `${dismissedCount} dismissed`,
      )
    : null;

  const wrap = h("div", {
    class: `inbox${live ? "" : " quiet"}`,
    "data-ref": `${INBOX_REF}:strip`,
  });

  const head = h("div", { class: "inbox-head" });
  if (!live) {
    // Nothing new — the strip is only here as the way back. One quiet line,
    // no title claiming news, no ✕ with nothing to dismiss.
    if (dismissedButton) head.append(dismissedButton);
    wrap.append(head);
    if (state.inboxShowDismissed) appendDismissed(wrap, inbox);
    return wrap;
  }
  head.append(
    h(
      "button",
      {
        class: "inbox-toggle",
        type: "button",
        title: state.inboxOpen ? "Collapse" : "Show what is new",
        "data-ref": `${INBOX_REF}:toggle`,
        onclick: () => {
          state.inboxOpen = !state.inboxOpen;
          render();
        },
      },
      rowsHere
        ? h("span", { class: "caret" }, state.inboxOpen ? "▾" : "▸")
        : null,
      h("span", { class: "inbox-title" }, "New for you"),
      // Counts as pills rather than as a sentence: two numbers in prose read as
      // one blur, and these are the two different jobs on offer.
      ...(retests
        ? [h("span", { class: "pill retest" }, `${retests} to retest`)]
        : []),
      ...(questions
        ? [
            h(
              "span",
              { class: "pill discussion" },
              `${questions} need${questions === 1 ? "s" : ""} your view`,
            ),
          ]
        : []),
      ...(replies
        ? [
            h(
              "span",
              { class: "pill reply" },
              `${replies} ${replies === 1 ? "reply" : "replies"}`,
            ),
          ]
        : []),
      ...(checklistChanged
        ? [h("span", { class: "pill" }, "checklist updated")]
        : []),
    ),
  );
  if (dismissedButton) head.append(dismissedButton);
  head.append(
    h(
      "button",
      {
        class: "icon",
        type: "button",
        title:
          "Stop showing the fixes listed here and the checklist change. Fixes claimed later still show up; replies stay until you open them; questions come back tomorrow until you answer them.",
        "data-ref": `${INBOX_REF}:dismiss`,
        onclick: () => void dismissInbox(),
      },
      "✕",
    ),
  );
  wrap.append(head);
  if (!state.inboxOpen || !rowsHere) {
    if (state.inboxShowDismissed) appendDismissed(wrap, inbox);
    return wrap;
  }

  const shown = state.inboxShowAll ? items : items.slice(0, INBOX_PREVIEW);
  for (const item of shown) wrap.append(renderInboxItem(item));
  if (items.length > INBOX_PREVIEW) {
    wrap.append(
      h(
        "button",
        {
          class: "turn-more",
          type: "button",
          "data-ref": `${INBOX_REF}:more`,
          onclick: () => {
            state.inboxShowAll = !state.inboxShowAll;
            render();
          },
        },
        state.inboxShowAll ? "Show fewer" : `Show all ${items.length}`,
      ),
    );
  }

  if (checklistChanged) {
    wrap.append(
      h(
        "div",
        { class: "inbox-row", "data-ref": `${INBOX_REF}:checklist` },
        h(
          "div",
          { class: "note" },
          "The checklist changed since you last looked — scenarios may have been added, reworded or retired.",
        ),
      ),
    );
  }

  if (state.inboxShowDismissed) appendDismissed(wrap, inbox);

  return wrap;
}

/**
 * The dismissed claims, under the live news, each with "Bring back".
 *
 * Fetched only while open (`getInbox(true)`), so the first render after the
 * toggle may be waiting on the wire; it says so rather than showing an empty
 * list that reads as "nothing to bring back".
 */
function appendDismissed(wrap: HTMLElement, inbox: api.TesterInbox): void {
  const rows = wireList(inbox.dismissed);
  const snoozed = wireList(inbox.dismissed_discussions);
  wrap.append(
    h(
      "div",
      { class: "inbox-sub", "data-ref": `${INBOX_REF}:dismissed-list` },
      rows.length || snoozed.length
        ? "Dismissed — still waiting for you"
        : (inbox.dismissed_count ?? 0)
          ? "Loading…"
          : "Nothing dismissed",
    ),
  );
  for (const retest of rows) {
    wrap.append(
      renderInboxItem(
        {
          findingId: retest.finding_id,
          url: retest.url,
          reply: null,
          retest,
          discussion: null,
        },
        { dismissed: true },
      ),
    );
  }
  // A question ✕ snoozed: back by itself tomorrow, and back NOW from here.
  for (const discussion of snoozed) {
    wrap.append(
      renderInboxItem(
        {
          findingId: discussion.finding_id,
          url: discussion.url,
          reply: null,
          retest: null,
          discussion,
        },
        { dismissed: true },
      ),
    );
  }
}

/**
 * One row: what happened, in whose words, and the one thing to do about it.
 *
 * The kind is carried by a coloured edge and a pill, in the SAME palette the
 * cards use — amber is a fix claim wherever you meet it (`.tag.status-fixed`,
 * the fix-note rule), blue is the tracker/conversation side. Without that the
 * two kinds were the same three greys in the same order and the only difference
 * was the sentence, which is not something an eye can sort on.
 */
function renderInboxItem(
  item: InboxItem,
  opts: { dismissed?: boolean } = {},
): Node {
  const { retest, reply, discussion } = item;
  const dismissed = Boolean(opts.dismissed);
  // A claim waiting on a verdict outranks an unread reply: it is a decision
  // only this tester can make, and it is what the edge colour should say. A
  // question is the same kind of decision, in its own colour.
  const kind = retest ? "retest" : discussion ? "discussion" : "reply";
  const row = h("div", {
    class: `inbox-row ${kind}${dismissed ? " dismissed" : ""}`,
    // Kept on the finding, not on the kind: one row per finding now, and a
    // spec that wants "the row about this finding" should not have to know
    // which kind of news arrived first. A dismissed row is its own ref: the
    // same finding can be live (an unread reply) AND dismissed (its claim).
    "data-ref": `${INBOX_REF}:${dismissed ? "dismissed" : "row"}-${item.findingId}`,
  });

  const badges = h("div", { class: "row" });
  if (retest) {
    badges.append(
      dismissed
        ? h("span", { class: "pill" }, "dismissed")
        : h("span", { class: "pill retest" }, "fix claimed"),
    );
  }
  if (discussion) {
    badges.append(
      dismissed
        ? h("span", { class: "pill" }, "snoozed")
        : h("span", { class: "pill discussion" }, "your view needed"),
    );
    // Said, not just coloured: it has been waiting more than a day, and a
    // reminder that looks exactly like fresh news is one the eye skips.
    if (discussion.reminder) {
      badges.append(
        h(
          "span",
          { class: "pill" },
          `reminder · asked ${discussion.asked_at.slice(0, 10)}`,
        ),
      );
    }
  }
  // On a question the unread message IS the question, so it gets no second
  // badge saying the same thing in another colour.
  if (reply && !discussion) {
    badges.append(
      h(
        "span",
        { class: "pill reply" },
        `${reply.unread} new ${reply.unread === 1 ? "reply" : "replies"}`,
      ),
    );
  }
  row.append(badges);

  // Who and where, in one quiet line. The route key, not the URL: this is the
  // orienting line, and the URL gets its own place at the foot of the row.
  const where =
    reply?.route_key ??
    discussion?.route_key ??
    wireList(retest?.route_keys)[0] ??
    null;
  row.append(
    h(
      "div",
      { class: "note" },
      [
        reply && !discussion
          ? `${reply.latest?.author ?? "someone"} replied`
          : null,
        retest ? "a developer says this is fixed" : null,
        discussion
          ? `${discussion.asked_by ?? "a developer"} asks before changing anything`
          : null,
        where ? `on ${where}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
    ),
  );

  // The words themselves — the reply if there is one, because that is somebody
  // talking to this tester; otherwise the fix note.
  const excerpt =
    reply?.latest?.body || discussion?.question || retest?.fix_note || "";
  if (excerpt) row.append(h("div", { class: "inbox-excerpt" }, excerpt));

  if (retest) {
    const build = buildNote(retest.fixed_in_build, retest.filed_in_build);
    if (build) row.append(build);
  }

  const focus: api.FindingFocus = {
    id: item.findingId,
    url: item.url,
    // A question is shown in its own block on the card, so the thread stays
    // folded: unfolded it printed the same words twice, one under the other.
    thread: Boolean(reply) && !discussion,
  };
  const actions = h("div", { class: "row", style: "margin-top:6px" });
  // A dismissed row's one verb is the way back; the job's own two steps are
  // still offered after it, because a claim can be answered from here too.
  if (dismissed) {
    actions.append(
      h(
        "button",
        {
          class: "btn",
          type: "button",
          title: "Put this back on the strip",
          "data-ref": `${INBOX_REF}:restore-${item.findingId}`,
          onclick: () => void restoreInboxRetest(item.findingId),
        },
        "Bring back",
      ),
    );
  }
  // Both steps of the job, in the order a tester does them. A fix claim LEADS
  // with the page — you cannot retest by reading — and a reply leads with the
  // words, because that is what it is about.
  if (retest) {
    // A claim made on a live preview (§7k) is retested THERE: the page the
    // finding was filed on still runs the old code. Optional on the wire — a
    // server older than the field sends none.
    const preview = retest.retest_url ?? null;
    const page = retestButton(
      preview ? { ...focus, url: preview } : focus,
      true,
      Boolean(preview),
    );
    if (page) actions.append(page);
    actions.append(
      openFindingButton(
        focus,
        "Open the finding to answer the claim",
        "Answer",
        false,
      ),
    );
  } else if (discussion) {
    // The words first: a question is answered by reading it, and the card
    // carries both answers (go ahead / not needed) and the reply box.
    actions.append(
      openFindingButton(
        focus,
        "Read the question and give your view",
        "Give your view",
      ),
    );
  } else {
    actions.append(
      openFindingButton(focus, "Read the conversation", "Read the reply"),
    );
    const page = retestButton(focus, false);
    if (page) actions.append(page);
  }
  row.append(actions);

  // Where that page IS, under the buttons: on a retest it is routinely another
  // environment, and the tester has to be able to see which before they go.
  row.append(inboxLink(item.url, "Open the page it was filed on"));
  return row;
}

/**
 * Whether a URL off the wire is one this widget may send a browser to at all.
 *
 * The SCHEME is the guard, not the origin. A finding's URL was stored by the
 * server from what a widget reported, and the danger in following one blind is
 * `javascript:` (which would run in the customer's page) or `data:` — not that
 * the host differs. A different host is the ordinary case here: a tester works
 * on localhost against a board filled from staging, and refusing to take them
 * to the page is refusing them the retest.
 */
function isFollowable(url: string): boolean {
  try {
    const parsed = new URL(url, location.href);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

function isSameOrigin(url: string): boolean {
  try {
    return new URL(url, location.href).origin === location.origin;
  } catch {
    return false;
  }
}

/**
 * A link into the page a finding was filed on.
 *
 * Same-origin ONLY, for the reason above. A foreign or unparseable URL is shown
 * as text.
 */
function inboxLink(url: string, label: string): Node {
  if (!isFollowable(url)) return h("div", { class: "ref" }, url);
  const foreign = !isSameOrigin(url);
  return h(
    "a",
    {
      class: "inbox-link",
      href: url,
      title: label,
      // A foreign page opens in its own tab, and never with a handle back into
      // this one: `noopener` is what stops the opened page reaching
      // `window.opener` on the customer's site.
      ...(foreign ? { target: "_blank", rel: "noopener noreferrer" } : {}),
    },
    // Foreign: the whole URL, because WHICH environment is the point. Same
    // origin: the path, because the origin is where you already are.
    foreign ? url : url.replace(location.origin, "") || "/",
  );
}

/**
 * "Open the finding" — the control every inbox row was missing.
 *
 * Always offered, on every row, whatever origin the finding was filed on: it
 * opens a CARD, not a page, so there is nothing here that could be an open
 * redirect and nothing that depends on the tester standing anywhere in
 * particular. (`inboxLink` below still refuses a foreign URL — following one is
 * a different act with a different risk.)
 */
function openFindingButton(
  focus: api.FindingFocus,
  title: string,
  label = "Open the finding",
  primary = true,
): Node {
  return h(
    "button",
    {
      class: primary ? "btn primary" : "btn",
      type: "button",
      title,
      "data-ref": `${INBOX_REF}:open-${focus.id}`,
      onclick: () => void openFinding(focus),
    },
    label,
  );
}

/**
 * "Retest it" — go to the page the problem was on.
 *
 * The first step of a retest, and for a while it was missing: the row opened
 * the CARD, which is where the claim reads and the verdict is given, and left
 * the page it is about as a grey line at the foot. But you cannot retest a page
 * by reading about it, and the tester's next move after "a developer says this
 * is fixed" is always to go and try it.
 *
 * Same origin: navigate, carrying a one-shot pointer so the widget on the other
 * side comes back with this finding open and its answer block in front of the
 * tester. They land on the page, try it, and say what happened without looking
 * for anything.
 *
 * Another origin: a new tab, and the pointer is NOT set — storage is per origin,
 * so the widget over there would never read it, and that page is a different
 * environment answering to a different deck. The card stays here, where the
 * verdict can still be given.
 */
function retestButton(
  focus: api.FindingFocus,
  primary: boolean,
  preview = false,
): Node | null {
  if (!isFollowable(focus.url)) return null;
  const here = isSameOrigin(focus.url);
  return h(
    "button",
    {
      class: primary ? "btn primary" : "btn",
      type: "button",
      title: preview
        ? `Open the live preview of the fix — ${focus.url}`
        : here
          ? "Go to the page it was filed on — the finding comes with you"
          : `Open ${focus.url} in a new tab — another environment, so the card stays here`,
      "data-ref": `${INBOX_REF}:page-${focus.id}`,
      onclick: () => {
        if (!here) {
          window.open(focus.url, "_blank", "noopener,noreferrer");
          return;
        }
        // One pin at a time: this replaces whatever was in hand.
        api.setFindingFocus({ ...focus, retest: true, fresh: true });
        // Or the tester lands on the right page with the panel shut.
        api.setPanelOpen(true);
        location.assign(focus.url);
      },
    },
    here ? "Retest it" : preview ? "Open the fix preview ↗" : "Open the page ↗",
  );
}

/**
 * Put one finding in front of the tester, wherever they are standing.
 *
 * On this page it is already rendered, so this is a scroll. Anywhere else the
 * card is FETCHED and pinned to the top of the tab — including when the finding
 * was filed on another environment entirely, which is the ordinary case for a
 * tester working locally against a board filled from staging.
 *
 * `quiet` is the reload of a held retest: the card comes back on its tab, but
 * nothing scrolls, flashes or unfolds — the tester did not ask for it just now.
 */
async function openFinding(
  focus: api.FindingFocus,
  { quiet = false } = {},
): Promise<void> {
  state.tab = "findings";
  // Same reason `setTab` does it: a conversation left open underneath would
  // strand the tester on a scenario they thought they had left.
  state.chat = null;

  // A mail link is always FETCHED, never read off the page's list: the list
  // may be minutes old (the tester was already on this page when they clicked
  // the mail), and what the link opens depends on the finding as it is now.
  const here = focus.linked
    ? undefined
    : state.findings.find((f) => f.id === focus.id);
  if (here && !focus.retest) {
    state.focus = null;
    render();
    if (!quiet) {
      revealFinding(focus.id);
      if (focus.thread && !state.threads.has(focus.id)) void toggleThread(here);
    }
    return;
  }
  // A retest is ALWAYS pinned, even when the finding is on this page's own
  // list (the list then skips it, so it shows once). Scrolling to the list card
  // instead held the pin invisibly, and the card left with the list on the next
  // client-side navigation — the card is the job, and the job goes where the
  // tester goes.

  state.focus = { ...focus, finding: null, loading: !here, error: null };
  const pinned = state.focus;
  render();
  try {
    const finding = here ?? (await api.getFinding(focus.id)).finding;
    // Release 3 of 3: nothing left to retest. Somebody else answered it, the
    // developer withdrew the claim, or it moved on — the pin is spent, and the
    // card says why rather than sitting there asking for a verdict that no
    // longer exists.
    if (focus.retest && finding.status !== "fixed") {
      api.releaseFindingFocus(focus.id);
      pinned.retest = false;
      pinned.error =
        finding.status === "resolved"
          ? "Already verified — nothing left to retest."
          : "No longer awaiting retest — the claim was withdrawn or the finding reopened.";
    } else {
      pinned.finding = finding;
      // A mail link decides here, on the finding as it is NOW: a fix awaiting
      // retest becomes the held pin "Retest it" would have made (released by
      // the verdict, Close, or the claim going away); anything else that has
      // a conversation opens it. A question shows its own block, so its thread
      // stays folded — the same rule the strip uses.
      if (focus.linked) {
        if (finding.status === "fixed") {
          pinned.retest = true;
          api.setFindingFocus({ id: finding.id, url: focus.url, retest: true });
        } else if (finding.status !== "needs_discussion") {
          pinned.thread = (finding.comments_count ?? 0) > 0;
        }
      }
    }
  } catch (error) {
    // Named, not swallowed: a row that opens nothing and says nothing is the
    // dead end this whole control exists to remove.
    const gone = error instanceof api.ApiClientError && error.status === 404;
    if (gone && focus.retest) api.releaseFindingFocus(focus.id);
    pinned.error = gone
      ? "That finding is no longer on this project."
      : error instanceof Error
        ? `Could not open the finding: ${error.message}`
        : "Could not open the finding";
  } finally {
    pinned.loading = false;
    // Only if the tester has not opened something else in the meantime.
    if (state.focus === pinned) {
      render();
      if (!quiet) {
        // By the FETCHED id: a link may name the finding by its `QA-142`.
        const id = pinned.finding?.id ?? focus.id;
        revealFinding(id);
        if (pinned.finding && pinned.thread && !state.threads.has(id)) {
          void toggleThread(pinned.finding);
        }
      }
    }
  }
}

/**
 * Put the tab back to the findings of the page the tester is on.
 *
 * Release 2 of 3 for a retest in hand: "Close" is "not now". The row is still
 * in the inbox and the retest queue, so nothing is lost — only the pin.
 */
function clearFocus(): void {
  if (state.focus?.retest) api.releaseFindingFocus(state.focus.id);
  state.focus = null;
  render();
}

/**
 * A finding this panel currently holds — the page's list, or the pinned one.
 *
 * Every path that updates a finding goes through here rather than reaching into
 * `state.findings` directly, or answering a fix claim from the inbox would
 * write to a list the pinned card is not in and leave it showing the old
 * status.
 */
function findingById(id: string): Finding | null {
  return (
    state.findings.find((f) => f.id === id) ??
    state.all.findings.find((f) => f.id === id) ??
    (state.focus?.finding?.id === id ? state.focus.finding : null)
  );
}

/** Write a server answer back into whichever of the two holds that finding. */
function replaceFinding(updated: Finding): void {
  state.findings = state.findings.map((f) =>
    f.id === updated.id ? updated : f,
  );
  // In place, never re-filtered: a finding answered by mistake must stay in
  // front of the tester, new status showing, so the undo is right there.
  state.all.findings = state.all.findings.map((f) =>
    f.id === updated.id ? updated : f,
  );
  if (state.focus?.finding?.id === updated.id) state.focus.finding = updated;
}

/**
 * Put one finding's card in front of the tester.
 *
 * Scrolled to its TOP, not centred, and to the top of the PINNED BLOCK when it
 * has one. A fix note routinely runs to twenty lines — the first real one did —
 * and centring such a card drops the tester into the middle of that text with
 * the tags, the words they wrote and "Filed on <url>" all above the fold: a
 * wall of prose with nothing saying what it belongs to.
 *
 * A flash, not a highlight that stays: the card is where the answer is given,
 * and a permanent marker on it would still be there next week saying nothing.
 */
function revealFinding(findingId: string): void {
  const card = root.querySelector<HTMLElement>(
    `[data-finding-id="${CSS.escape(findingId)}"]`,
  );
  if (!card) return;
  (card.closest(".pinned") ?? card).scrollIntoView({ block: "start" });
  card.animate?.(
    [{ background: "rgba(124,92,255,.22)" }, { background: "transparent" }],
    { duration: 900, easing: "ease-out" },
  );
}

/**
 * "Is this fix in front of me yet?"
 *
 * The one question a retest actually asks, and `env` could never answer it: a
 * fix claimed on an unmerged branch is absent from the staging the tester is
 * looking at, so following the row shows them the OLD behaviour and invites a
 * false "still broken".
 *
 * The deck decides nothing here beyond equality. Only the consumer's own git
 * knows whether the build on screen contains a given commit, so a mismatch is
 * REPORTED, never used to hide the row: a fix that landed three builds ago
 * would otherwise be suppressed exactly as hard as one that has not shipped.
 * Silent when either side has no build — that is every consumer without a
 * qa-context probe, and it is the behaviour they had before this existed.
 */
function buildNote(
  fixedIn: string | null | undefined,
  filedIn: string | null | undefined,
): Node | null {
  if (!fixedIn) return null;
  const build = getBuild();
  if (!build) {
    // Nothing to compare against: say where the fix is and let the tester judge.
    return h("div", { class: "note" }, `Fixed in ${fixedIn}.`);
  }
  const current = build.sha;
  if (isSameBuild(current, fixedIn)) {
    return h(
      "div",
      { class: "note" },
      `Fixed in ${fixedIn} — the build you are on.`,
    );
  }
  // The consumer's git said this build is built on top of that one. Not a
  // warning: the fix is in front of the tester, and saying otherwise here
  // collected fifteen false "still broken" invitations in one afternoon.
  if (buildCarries(build, fixedIn)) {
    return h(
      "div",
      { class: "note" },
      `Fixed in ${fixedIn}; this environment is running ${current}, which includes it.`,
    );
  }
  // Not known to carry it — which is NOT "known not to": the consumer's list
  // is a window of recent builds, and an older fix falls outside it. Hedge
  // exactly that far.
  return h(
    "div",
    { class: "note build-warn" },
    `Fixed in ${fixedIn}; this environment is running ${current}${
      filedIn && isSameBuild(filedIn, current)
        ? " — the same build you filed on"
        : ""
    }. If that fix has not been deployed here, you will still see the old behaviour.`,
  );
}

function renderChecklist(): Node {
  const wrap = h("div", {});

  const inbox = renderInbox();
  if (inbox) wrap.append(inbox);

  wrap.append(
    h(
      "p",
      { class: "note" },
      "Expand a scenario to see its steps and what to expect. Mark it ✓ passed, ✕ failed (opens a linked finding), or ↷ skipped.",
    ),
  );

  if (state.retestTotal) {
    const here =
      retestOf(state.items).length +
      retestOf(state.otherViewItems).length +
      retestOf(state.globalItems).length;
    wrap.append(
      h(
        "div",
        { style: "margin-bottom:10px" },
        h(
          "button",
          {
            class: state.retestOnly ? "btn primary" : "btn",
            type: "button",
            "data-ref": "QaDeck:Checklist:retest-filter",
            onclick: () => {
              state.retestOnly = !state.retestOnly;
              render();
            },
          },
          state.retestOnly
            ? "Showing retests only"
            : `Needs retest (${state.retestTotal})`,
        ),
        // The count that matters is the one on THIS page; the total is what
        // stops a tester concluding the queue is empty because this page is.
        h(
          "p",
          { class: "note" },
          here
            ? `${here} of ${state.retestTotal} fixed scenario(s) are on this page.`
            : `None on this page — ${state.retestTotal} waiting on other surfaces.`,
        ),
      ),
    );
  }

  if (state.retestOnly) {
    const buckets: [string, ChecklistItem[]][] = [
      [`Retest on this page`, retestOf(state.items)],
      [`Retest on other views of this page`, retestOf(state.otherViewItems)],
      [`Retest — general checks`, retestOf(state.globalItems)],
    ];
    let shown = 0;
    for (const [label, items] of buckets) {
      if (!items.length) continue;
      shown += items.length;
      wrap.append(renderGroup(`${label} (${items.length})`, items, true));
    }
    if (!shown) {
      wrap.append(
        h(
          "p",
          { class: "note" },
          "Nothing to retest on this page. Navigate to a surface that has one, or switch the filter off.",
        ),
      );
    }
    return wrap;
  }

  if (
    !state.items.length &&
    !state.otherViewItems.length &&
    !state.globalItems.length
  ) {
    wrap.append(
      h(
        "p",
        { class: "note" },
        `No checklist scenarios are written for ${state.routeKey} yet — explore freely and report anything odd from the Findings tab.`,
      ),
    );
  }

  for (const { label, items } of groupBySpec(state.items).values()) {
    wrap.append(renderGroup(label, items, true));
  }

  if (state.otherViewItems.length) {
    wrap.append(
      renderGroup(
        `Other views of this page (${state.otherViewItems.length})`,
        state.otherViewItems,
        false,
      ),
    );
  }

  if (state.globalItems.length) {
    wrap.append(
      renderGroup(
        `General checks — not specific to this page (${state.globalItems.length})`,
        state.globalItems,
        false,
      ),
    );
  }

  return wrap;
}

function renderGroup(
  title: string,
  items: ChecklistItem[],
  open: boolean,
): Node {
  const done = items.filter(
    (i) => (state.itemState.get(i.id)?.status ?? "unchecked") !== "unchecked",
  ).length;
  const details = h("details", {
    class: "group",
    open: open ? "open" : undefined,
  });
  details.append(
    h(
      "summary",
      {},
      title,
      h("span", { class: "count" }, `${done}/${items.length}`),
    ),
  );
  for (const item of items) details.append(renderItem(item));
  return details;
}

function renderItem(item: ChecklistItem): Node {
  const current = state.itemState.get(item.id)?.status ?? "unchecked";
  const expanded = state.expanded.has(item.id);

  const statusButton = (
    status: ChecklistItemStatus,
    glyph: string,
    title: string,
  ) =>
    h(
      "button",
      {
        class: `btn${current === status ? " active" : ""}`,
        type: "button",
        title,
        "data-ref": `QaDeck:Checklist:${status}-${item.id}`,
        onclick: () =>
          void setItemStatus(item, current === status ? "unchecked" : status),
      },
      glyph,
    );

  const el = h(
    "div",
    { class: "item", "data-status": current, "data-item-id": item.id },
    h(
      "div",
      { class: "head" },
      h(
        "div",
        {
          class: "name",
          "data-ref": `QaDeck:Checklist:toggle-${item.id}`,
          onclick: () => {
            if (expanded) state.expanded.delete(item.id);
            else state.expanded.add(item.id);
            render();
          },
        },
        itemLabel(item),
      ),
      h(
        "div",
        { class: "statuses" },
        statusButton("passed", "✓", "Passed"),
        statusButton("failed", "✕", "Failed — opens a linked finding"),
        statusButton("skipped", "↷", "Skipped"),
        // After the verdict trio, not among it: asking what a scenario means
        // is not a fourth verdict, and it records nothing about the scenario.
        chatButton(item, null),
      ),
    ),
  );

  // Say WHY this row is in the retest queue, so the badge is a reason rather
  // than decoration — the failure is not in this session's state to look at.
  if (state.retest.has(item.id) && current === "unchecked") {
    el.append(
      h(
        "div",
        { class: "note", "data-ref": `QaDeck:Checklist:retest-${item.id}` },
        "Failed earlier; a developer marked the finding fixed. Re-run it — ✓ verifies the fix, ✕ reopens the finding.",
      ),
    );
  }

  if (expanded) {
    const detail = h("div", { class: "detail" });

    /**
     * Steps are read-only text. They carried a tick box each until step results
     * moved onto the FINDING: how a step went is evidence about a failure, and
     * it is asked for once, in the ✕ form, where "I never got there" is a
     * meaningful answer. A running tally on the checklist asked the same
     * question at every line and recorded nothing anyone reads afterwards.
     */
    const list = (
      heading: string,
      entries: string[],
      ordered: boolean,
      /** Steps get a per-step ⓘ; expectations and hidden checks do not. */
      askable = false,
    ) => {
      if (!entries.length) return;
      detail.append(h("h4", {}, heading));
      const listEl = h(ordered ? "ol" : "ul", {
        class: askable ? "steps" : undefined,
      });
      entries.forEach((entry, index) => {
        listEl.append(
          askable
            ? h(
                "li",
                {},
                h("span", { class: "step-text" }, entry),
                // Per STEP as well as per scenario: "click Save" is clear, "the
                // draft must survive a reload" is the line someone gets stuck
                // on, and a question about it needs that step as its subject.
                chatButton(item, index),
              )
            : h("li", {}, entry),
        );
      });
      detail.append(listEl);
    };
    // When the row shows a rewrite, the spec's own wording is still one click
    // away. The rewrite is enrichment; the author's title is the record, and a
    // tester who suspects the paraphrase drifted needs to be able to check.
    if (item.qaTitle?.trim() && item.qaTitle.trim() !== item.title) {
      detail.append(
        h("h4", {}, "Spec title"),
        h("div", { class: "ref" }, item.title),
      );
    }
    list("Steps", item.steps, true, true);
    list("Expected", item.expected, false);
    list("Hidden state checks", item.hiddenStateChecks, false);
    if (item.featureFlags.length) {
      detail.append(
        h("h4", {}, "Feature flags"),
        h("div", { class: "ref" }, item.featureFlags.join(", ")),
      );
    }
    if (item.filesInvolved.length) {
      detail.append(
        h("h4", {}, "Files involved"),
        h("div", { class: "ref" }, item.filesInvolved.join("\n")),
      );
    }
    detail.append(renderItemNote(item));
    el.append(detail);
  }

  return el;
}

/**
 * The scenario's own note, at the bottom of the item and on the MAIN item only.
 *
 * Deliberately not per step: a note is about the check, and splitting it across
 * steps would scatter one observation over eight boxes and leave the reader
 * reassembling it. Available at every status, not just before a verdict — "this
 * passed, but only on the second try" is the sentence most worth keeping, and
 * it can only be written after the ✓.
 *
 * Saved on blur rather than per keystroke; `noteDrafts` holds what is typed in
 * between, so a re-render (a tick, an upload) never eats an unsaved sentence.
 */
function renderItemNote(item: ChecklistItem): Node {
  const stored = state.itemState.get(item.id);
  const attachments = wireList(stored?.attachments);

  const note = h("textarea", {
    placeholder: "Notes on this scenario — what you saw, what to watch for…",
    "data-ref": `QaDeck:Checklist:note-${item.id}`,
  }) as HTMLTextAreaElement;
  note.value = noteDrafts.get(item.id) ?? stored?.note ?? "";
  note.addEventListener("input", () => noteDrafts.set(item.id, note.value));
  note.addEventListener("change", () => {
    noteDrafts.delete(item.id);
    void saveItemState(item, { note: note.value.trim() || null });
  });

  const onImages = (ids: string[]) => {
    // Whatever is in the box goes with the picture: an image attached mid-
    // sentence must not save an empty note over what is still being typed.
    const typed = noteDrafts.get(item.id);
    void saveItemState(item, {
      attachment_ids: ids,
      ...(typed === undefined ? {} : { note: typed.trim() || null }),
    });
  };
  enableImagePaste(
    note,
    () => wireList(state.itemState.get(item.id)?.attachments),
    onImages,
  );

  return h(
    "div",
    {},
    h("h4", {}, "Notes"),
    note,
    renderAttachments({
      refPrefix: `QaDeck:Checklist:note-${item.id}`,
      ids: attachments,
      onChange: onImages,
    }),
  );
}

/** Unsaved note text, keyed by item id — see `renderItemNote`. */
const noteDrafts = new Map<string, string>();

// -------------------------------------------------------------- item chat --

/**
 * "What is this scenario asking me to do?"
 *
 * The outlet a tester who does not understand a row otherwise lacks: guessing,
 * or interrupting the team. The answers are grounded in the spec text alone,
 * and every conversation is kept so the spec author can see WHICH items confuse
 * people — the conversations are the product as much as the answers are.
 *
 * Absent, not disabled, when the org has no entitlement or the deployment has
 * no model: `itemChatEnabled` decides whether the ⓘ exists at all, because a
 * button that always errors is worse than no button.
 */
const CHAT_REF = "QaDeck:Chat";

/** Unsent question text, kept across re-renders — see `sendChatMessage`. */
let chatDraft = "";

function chatButton(
  item: ChecklistItem,
  stepIndex: number | null,
): Node | null {
  if (!state.config?.itemChatEnabled) return null;
  return h(
    "button",
    {
      class: "btn chat-open",
      type: "button",
      title:
        stepIndex === null
          ? "Ask what this scenario is asking you to do"
          : "Ask what this step means",
      "data-ref": `${CHAT_REF}:open-${item.id}${
        stepIndex === null ? "" : `-${stepIndex}`
      }`,
      onclick: () => void openItemChat(item, stepIndex),
    },
    "ⓘ",
  );
}

/** Is `chat` still the conversation the tester is looking at? */
function isCurrentChat(chat: ChatView): boolean {
  return (
    state.chat?.item.id === chat.item.id &&
    state.chat?.stepIndex === chat.stepIndex
  );
}

async function openItemChat(
  item: ChecklistItem,
  stepIndex: number | null,
): Promise<void> {
  if (!state.session) return;
  const chat: ChatView = {
    item,
    stepIndex,
    thread: null,
    loading: true,
    sending: false,
    error: null,
  };
  state.chat = chat;
  chatDraft = "";
  render();

  try {
    const { thread } = await api.getItemChat(
      state.session.id,
      item.id,
      stepIndex,
    );
    chat.thread = thread;
  } catch (error) {
    // A thread that would not load is not a reason to refuse the question —
    // asking still works, it just starts without the earlier turns on screen.
    chat.error =
      error instanceof Error
        ? `Could not load the earlier questions: ${error.message}`
        : "Could not load the earlier questions";
  } finally {
    chat.loading = false;
    if (isCurrentChat(chat)) render();
  }
}

/**
 * Send one question.
 *
 * The draft is cleared ONLY on success. A failed send leaves the words in the
 * box: there is nothing to queue (rule 8 covers evidence, and a question has no
 * value without an answer), so the tester's retry must not start from a blank
 * box — see `server/item-chat.ts` for the server half of the same rule.
 */
async function sendChatMessage(): Promise<void> {
  const chat = state.chat;
  if (!chat || !state.session) return;
  const message = chatDraft.trim();
  if (!message || chat.sending) return;

  chat.sending = true;
  chat.error = null;
  render();

  try {
    const { thread } = await api.sendItemChat(
      state.session.id,
      chat.item.id,
      chat.stepIndex,
      message,
    );
    chat.thread = thread;
    chatDraft = "";
  } catch (error) {
    chat.error =
      error instanceof Error ? error.message : "Could not ask that question";
  } finally {
    chat.sending = false;
    if (isCurrentChat(chat)) {
      render();
      focusChatInput();
    }
  }
}

/** Put the caret back in the box and the newest turn in view. */
function focusChatInput(): void {
  const input = root.querySelector<HTMLTextAreaElement>(
    `[data-ref="${CHAT_REF}:input"]`,
  );
  input?.focus();
  bodyEl.scrollTop = bodyEl.scrollHeight;
}

function renderItemChat(chat: ChatView): Node {
  const wrap = h("div", { "data-ref": `${CHAT_REF}:view` });

  wrap.append(
    h(
      "div",
      { style: "margin-bottom:8px" },
      h(
        "button",
        {
          class: "btn",
          type: "button",
          "data-ref": `${CHAT_REF}:back`,
          onclick: () => {
            state.chat = null;
            render();
          },
        },
        "← Back to the checklist",
      ),
    ),
    h("label", {}, "Asking about"),
    h("div", { class: "chat-subject" }, itemLabel(chat.item)),
  );

  if (chat.stepIndex !== null) {
    wrap.append(
      h(
        "div",
        { class: "chat-subject step", "data-ref": `${CHAT_REF}:step` },
        `Step ${chat.stepIndex + 1}: ${chat.item.steps[chat.stepIndex] ?? ""}`,
      ),
    );
  }

  wrap.append(
    h(
      "p",
      { class: "note" },
      "Answers come from this scenario's spec text only. If the spec doesn't " +
        "cover your question, you'll be told to ask the team — and the question " +
        "is kept, so the wording can be improved.",
    ),
  );

  if (chat.error) wrap.append(h("div", { class: "error" }, chat.error));

  const log = h("div", { class: "chat-log", "data-ref": `${CHAT_REF}:log` });
  if (chat.loading) {
    log.append(h("p", { class: "note" }, "Loading…"));
  } else if (!chat.thread?.messages.length) {
    log.append(
      h(
        "p",
        { class: "note" },
        "No questions yet. Ask anything about what this is checking.",
      ),
    );
  } else {
    for (const message of chat.thread.messages) {
      log.append(
        h(
          "div",
          { class: `chat-msg ${message.role}` },
          h(
            "div",
            { class: "chat-who" },
            message.role === "tester" ? "you" : "assistant",
          ),
          h("div", { class: "chat-text" }, message.content),
        ),
      );
    }
  }
  wrap.append(log);

  const input = h("textarea", {
    placeholder: "e.g. what counts as “the draft survives a reload”?",
    "data-ref": `${CHAT_REF}:input`,
  }) as HTMLTextAreaElement;
  input.value = chatDraft;
  input.addEventListener("input", () => {
    chatDraft = input.value;
  });
  // Enter sends, Shift+Enter breaks the line: this is a chat box, and a tester
  // mid-question should not have to reach for the mouse.
  input.addEventListener("keydown", (event) => {
    const key = event as KeyboardEvent;
    if (key.key === "Enter" && !key.shiftKey) {
      key.preventDefault();
      void sendChatMessage();
    }
  });

  wrap.append(
    h("label", {}, "Your question"),
    input,
    h(
      "div",
      { style: "margin-top:8px" },
      h(
        "button",
        {
          class: "btn primary",
          type: "button",
          "data-ref": `${CHAT_REF}:send`,
          disabled: chat.sending,
          onclick: () => void sendChatMessage(),
        },
        chat.sending ? "Asking…" : "Ask",
      ),
    ),
  );

  return wrap;
}

/**
 * Write one scenario's note / ticks / images, leaving the verdict as it is.
 *
 * The status has to be sent (it is the row's one required column) but is read
 * back from local state rather than changed, and this path never runs the
 * "failed opens a finding" side effect — saving a note is not giving a verdict.
 */
async function saveItemState(
  item: ChecklistItem,
  // `checked_steps` is still part of the wire shape (older sessions keep their
  // ticks) but nothing in the panel writes it any more — see `renderItem`.
  patch: { note?: string | null; attachment_ids?: string[] },
): Promise<void> {
  if (!state.session) return;
  const current = state.itemState.get(item.id)?.status ?? "unchecked";
  try {
    const updated = await api.setChecklistState(
      state.session.id,
      item.id,
      current,
      state.routeKey,
      patch,
    );
    state.itemState.set(item.id, updated);
    // Deliberately NOT clearing `state.error` on success: an image that failed
    // to upload writes its error and then saves the (unchanged) id list, so
    // clearing here erased the only message saying the screenshot never landed.
  } catch (error) {
    state.error =
      error instanceof Error ? error.message : "Could not save that note";
  }
  renderKeepingFocus();
}

async function setItemStatus(
  item: ChecklistItem,
  status: ChecklistItemStatus,
): Promise<void> {
  if (!state.session) return;
  try {
    const updated = await api.setChecklistState(
      state.session.id,
      item.id,
      status,
      state.routeKey,
    );
    state.itemState.set(item.id, updated);
    // A verdict is the only thing that moves a scenario in or out of the retest
    // queue, so this is exactly when the cached copy stops being true.
    state.retestLoaded = false;
    if (status === "failed") {
      // Marking failed prompts for the linked finding, pre-filled with what
      // the scenario said SHOULD happen — the tester only has to say what did.
      state.linkedItemId = item.id;
      draft.observed ||= "";
      draft.expected ||= item.expected.join("\n");
      // Replaced rather than merged, unlike `expected` above: these steps must
      // be the steps of the scenario the finding is LINKED to, and ✕ on a
      // second scenario re-links it. Every step starts unmarked — "not
      // reached" is the truth until the tester says otherwise.
      draft.stepResults = item.steps.map((text) => ({ text, status: null }));
      state.tab = "findings";
    } else if (state.linkedItemId === item.id) {
      // The scenario is no longer failed, so a finding form still tied to it is
      // stale: ✕ pressed by mistake and corrected to ✓ must not leave the next
      // finding filed against it. The tester's typed words stay.
      unlinkDraft();
    }
    render();
    if (status !== "failed") {
      // ✓ and ↷ record and move on. No popup, no per-step questions — just an
      // acknowledgement, because the verdict itself is the whole interaction.
      flashStatus(
        status === "passed"
          ? "✓ passed"
          : status === "skipped"
            ? "↷ skipped"
            : "verdict cleared",
      );
      flashItem(item.id);
    }
  } catch (error) {
    state.error =
      error instanceof Error ? error.message : "Could not save checklist state";
    render();
  }
}

// ---------------------------------------------------------------- findings --

function renderFindings(): Node {
  const wrap = h("div", {});
  // News belongs on BOTH tabs. It used to render only from the checklist, so a
  // tester who opened the panel on Findings — or who arrived here by following
  // a row — saw no strip at all and reasonably concluded there was no inbox.
  // Only one tab renders at a time, so this shows it, never doubles it.
  const inbox = renderInbox();
  if (inbox) wrap.append(inbox);
  // The card the tester ASKED for goes above the blank form, not under it: a
  // reload mid-retest put "Retest in progress" below the strip and the empty
  // new-finding form, off the bottom of a laptop screen, and the tester
  // reasonably concluded the card was gone after all.
  const pinned = renderPinnedFinding();
  if (pinned) wrap.append(pinned);
  wrap.append(renderNewFinding());

  wrap.append(h("label", {}, `Findings on ${state.routeKey}`));
  if (!state.findings.length)
    wrap.append(h("p", { class: "note" }, "None yet."));
  for (const finding of state.findings) {
    // Shown once: a retest in hand is pinned above, even on its own page.
    if (finding.id === state.focus?.finding?.id) continue;
    wrap.append(renderFinding(finding));
  }
  return wrap;
}

/**
 * The finding an inbox row opened, above the page's own list.
 *
 * Separated from the list and labelled with the URL it was filed on, because
 * they are different claims: the list is "what has been reported HERE", and
 * this is "the one you were asked about" — often on another environment
 * entirely. Merging them would quietly tell the tester that a staging finding
 * is something they are looking at.
 */
function renderPinnedFinding(): Node | null {
  const focus = state.focus;
  if (!focus) return null;

  const wrap = h("div", {
    class: "pinned",
    "data-ref": "QaDeck:Findings:pinned",
  });
  // A retest in hand is named as one, and says it will stay: the tester who
  // just watched a reload swallow the card needs to be told that this one
  // survives page loads, and that "Close" is how it stops.
  wrap.append(
    h(
      "div",
      { class: "pinned-head" },
      h("label", {}, focus.retest ? "Retest in progress" : "From your inbox"),
      h(
        "button",
        {
          class: "btn",
          type: "button",
          title: focus.retest
            ? "Put this retest down — it stays in your inbox"
            : "Back to the findings on this page",
          "data-ref": "QaDeck:Findings:pinned-close",
          onclick: () => clearFocus(),
        },
        "Close",
      ),
    ),
  );
  // Always stated, never assumed: the card's words describe the page it was
  // filed on, which may not be the one the tester is looking at.
  wrap.append(h("div", { class: "ref" }, `Filed on ${focus.url}`));
  if (focus.retest) {
    wrap.append(
      h(
        "p",
        { class: "note" },
        "Pinned until you answer, press Close, or the claim is settled elsewhere. It follows you across page loads.",
      ),
    );
    // The way back to the page: a retest that bounced the tester somewhere
    // else (a redirect, a login) must not leave them looking for the URL.
    const back = retestButton(focus, false);
    if (back) wrap.append(h("div", { class: "row" }, back));
  }

  if (focus.loading) wrap.append(h("p", { class: "note" }, "Opening…"));
  else if (focus.error) wrap.append(h("div", { class: "error" }, focus.error));
  else if (focus.finding) wrap.append(renderFinding(focus.finding));
  return wrap;
}

/**
 * The ONE finding form, at the top of the Findings tab.
 *
 * Writing a new finding and editing a filed one are the same act on the same
 * fields, so they are the same form: `state.editingFindingId` decides which
 * finding it is pointed at. The alternative — a second editor unfolding under
 * whichever card you pressed — split the same six fields across two places
 * and put the smaller one at the bottom of a scrolling list.
 */
function renderNewFinding(): Node {
  const editing = state.editingFindingId
    ? findingById(state.editingFindingId)
    : null;
  // Rule 2: a reviewer may change severity and the comment; the tester's own
  // words are theirs alone, and `correctFinding` is the only door to them.
  const isAuthor = !editing || editing.session.tester === state.config?.tester;

  const severity = h("select", { "data-ref": "QaDeck:Finding:severity" });
  for (const value of ["low", "medium", "high"] as Severity[]) {
    const option = h("option", { value }, value);
    if (draft.severity === value) option.selected = true;
    severity.append(option);
  }
  severity.addEventListener("change", () => {
    draft.severity = severity.value as Severity;
  });

  const observed = h("textarea", {
    placeholder: "What happened?",
    "data-ref": "QaDeck:Finding:observed",
  });
  observed.value = draft.observed;
  observed.addEventListener("input", () => {
    draft.observed = observed.value;
  });

  const expected = h("textarea", {
    placeholder: "What should have happened?",
    "data-ref": "QaDeck:Finding:expected",
  });
  expected.value = draft.expected;
  expected.addEventListener("input", () => {
    draft.expected = expected.value;
  });

  if (!isAuthor) {
    observed.disabled = true;
    expected.disabled = true;
  }

  const comment = h("textarea", {
    placeholder: "Optional note — paste a screenshot here to attach it",
    "data-ref": "QaDeck:Finding:comment",
  });
  comment.value = draft.comment;
  comment.addEventListener("input", () => {
    draft.comment = comment.value;
  });

  const onImages = (ids: string[]) => {
    draft.attachments = ids;
    renderKeepingFocus();
  };
  // Every box a tester types evidence into accepts a pasted screenshot; which
  // one they happen to be in when they hit paste is not a decision worth
  // making. Not while editing: attachments have no patch shape, so a pasted
  // screenshot would upload and then quietly fail to belong to anything.
  if (!editing) {
    for (const field of [observed, expected, comment]) {
      enableImagePaste(field, () => draft.attachments, onImages);
    }
  }

  const maxPicks = state.config?.maxPickedElements ?? 1;
  const pickInfo = state.picks.length
    ? h(
        "div",
        { class: "picked-list", "data-ref": "QaDeck:Finding:picked" },
        ...state.picks.map((pick, i) =>
          h(
            "div",
            { class: "picked", "data-ref": `QaDeck:Finding:picked-${i + 1}` },
            h("span", { class: "picked-n" }, String(i + 1)),
            h(
              "span",
              { class: "ref picked-ref" },
              pick.anchor.anchorRef ?? pick.anchor.selector,
            ),
            h(
              "button",
              {
                class: "picked-x",
                type: "button",
                title: "Remove this element from the finding",
                "aria-label": `Remove element ${i + 1}`,
                "data-ref": `QaDeck:Finding:unpick-${i + 1}`,
                onclick: () => {
                  state.picks = state.picks.filter((_, at) => at !== i);
                  refreshPickMarkers();
                  renderKeepingFocus();
                },
              },
              "✕",
            ),
          ),
        ),
      )
    : h(
        "div",
        { class: "ref", style: "margin-top:6px" },
        "no element attached",
      );

  const pickingOne = isPicking() && !isPickingMultiple();
  const pickButton = h(
    "button",
    {
      class: `btn${pickingOne ? " active" : ""}`,
      type: "button",
      title: "Click an element on the page to attach it to this finding",
      "data-ref": "QaDeck:Finding:pick",
      onclick: () => {
        const wasOne = pickingOne;
        if (isPicking()) stopPick();
        if (wasOne) return render();
        startPick(
          (result) => {
            state.picks = [result];
            render();
          },
          () => render(),
        );
      },
    },
    pickingOne
      ? "Picking… (Esc)"
      : state.picks.length
        ? "Re-pick element"
        : "Pick element",
  );

  // Several elements in ONE sequence: pick mode stays on between clicks, the
  // picked ones are numbered on the page, a second click on one removes it,
  // and it stops by itself at the limit. Only offered when the server says it
  // keeps them (`maxPickedElements`) — an older one would drop all but one.
  const pickingMany = isPickingMultiple();
  const full = state.picks.length >= maxPicks;
  const pickManyButton =
    maxPicks > 1
      ? h(
          "button",
          {
            class: `btn${pickingMany ? " active" : ""}`,
            type: "button",
            disabled: !pickingMany && full,
            title: full
              ? `A finding can point at ${maxPicks} elements at most`
              : `Click up to ${maxPicks} elements in a row; click a numbered one again to remove it`,
            "data-ref": "QaDeck:Finding:pick-multiple",
            onclick: () => {
              if (pickingMany) {
                stopPick();
                return render();
              }
              if (isPicking()) stopPick();
              startPick(
                (result) => {
                  if (state.picks.length >= maxPicks) return;
                  state.picks = [...state.picks, result];
                  render();
                },
                () => render(),
                {
                  max: maxPicks,
                  picked: () => state.picks,
                  onUnpick: (index) => {
                    state.picks = state.picks.filter((_, at) => at !== index);
                    render();
                  },
                },
              );
            },
          },
          pickingMany
            ? `Done (${state.picks.length} of ${maxPicks})`
            : state.picks.length
              ? `Pick more (${state.picks.length} of ${maxPicks})`
              : `Pick multiple (max ${maxPicks})`,
        )
      : null;

  const linked = state.linkedItemId
    ? h(
        "div",
        { style: "margin-top:6px; display:flex; align-items:center; gap:6px" },
        h(
          "span",
          { class: "note", style: "flex:1" },
          `linked to checklist item: ${state.linkedItemId}`,
        ),
        h(
          "button",
          {
            class: "btn",
            type: "button",
            title:
              "This finding is not about that scenario — drop the link and its steps",
            "data-ref": "QaDeck:Finding:unlink",
            onclick: () => {
              unlinkDraft();
              renderKeepingFocus();
            },
          },
          "Unlink",
        ),
      )
    : null;

  const steps = renderDraftSteps();

  // Where the bug should land, decided here rather than by a reviewer later:
  // the tester on the page is the one who knows the sprint and the story.
  const tracker = h("div", {});
  if (state.config?.issueTrackerConfigured) {
    void ensureIssueTargets();
    const filedIssue = editing?.issue ?? null;

    const fileToggle = h("input", {
      type: "checkbox",
      "data-ref": "QaDeck:Finding:file-toggle",
    }) as HTMLInputElement;
    fileToggle.checked = draft.fileToTracker;

    // The four target fields are a question only once the tester has said yes:
    // unchecked means the finding stays in qadeck, and a sprint picker sitting
    // above that box asks where to put a work item nobody is creating.
    const fields = h("div", { "data-ref": "QaDeck:Finding:tracker-fields" });
    // An already-filed finding's targets are always visible: the checkbox
    // gated the decision to file, and that decision is behind it.
    const syncFields = () => {
      fields.style.display = filedIssue || draft.fileToTracker ? "" : "none";
    };

    fileToggle.addEventListener("change", () => {
      draft.fileToTracker = fileToggle.checked;
      syncFields();
    });

    // The box is the gate, so a field can only be touched with filing already
    // on — there is no longer an intent to infer from a sprint choice.
    const touched = (apply: (value: string) => void) => (value: string) => {
      apply(value);
      render();
    };

    tracker.append(
      filedIssue
        ? h("label", {}, `Work item #${filedIssue.id}`)
        : h(
            "label",
            {
              style:
                "display:flex; align-items:center; gap:6px; margin-top:10px",
            },
            fileToggle,
            h(
              "span",
              {},
              // "File to the issue tracker" told a tester nothing about what it
              // would do. Name the system and the object it creates.
              `Also create a work item in ${trackerLabel()}`,
            ),
          ),
      fields,
    );

    fields.append(
      targetField(
        "Work item type",
        "QaDeck:Finding:new-type",
        state.issueTargets?.types,
        state.target.workItemType,
        "Bug",
        touched((value) => {
          state.target.workItemType = value;
        }),
      ),
      targetField(
        "Assign to",
        "QaDeck:Finding:new-assignee",
        state.issueTargets?.assignees,
        state.target.assignee,
        "name@example.com",
        touched((value) => {
          state.target.assignee = value;
        }),
      ),
      targetField(
        "Sprint",
        "QaDeck:Finding:new-iteration",
        state.issueTargets?.iterations,
        state.target.iteration,
        "Project\\Sprint 3",
        touched((value) => {
          state.target.iteration = value;
        }),
      ),
      targetField(
        "Parent story",
        "QaDeck:Finding:new-parent",
        state.issueTargets?.parents,
        state.target.parentId,
        "1234",
        touched((value) => {
          state.target.parentId = value;
        }),
      ),
    );

    // Carried over from the retired per-card editor, and now available while
    // WRITING a finding too: the story a bug belongs under often does not
    // exist yet, and re-opening a list to find what you just made is the kind
    // of friction that stops people making one.
    if (state.issueTargets) {
      fields.append(
        h("label", {}, "Or create a story"),
        h("input", {
          type: "text",
          placeholder: "New story title…",
          value: state.newStory,
          "data-ref": "QaDeck:Finding:new-story",
          oninput: (event: Event) => {
            state.newStory = (event.target as HTMLInputElement).value;
          },
        }),
        h(
          "button",
          {
            class: "btn",
            type: "button",
            style: "margin-top:6px",
            "data-ref": "QaDeck:Finding:new-story-add",
            onclick: async () => {
              const title = state.newStory.trim();
              if (!title) return;
              try {
                const { parent } = await api.createIssueParent(title);
                // Select it immediately — see above.
                if (state.issueTargets) {
                  state.issueTargets.parents = [
                    parent,
                    ...state.issueTargets.parents,
                  ];
                }
                state.target.parentId = parent.id;
                state.newStory = "";
                flashStatus(`created ${parent.label}`);
              } catch (error) {
                state.error =
                  error instanceof Error ? error.message : "Could not create";
              }
              render();
            },
          },
          "Create story",
        ),
      );
    }

    if (filedIssue) {
      fields.append(
        h(
          "div",
          { style: "margin-top:8px" },
          h(
            "button",
            {
              class: "btn",
              type: "button",
              "data-ref": "QaDeck:Finding:update-issue",
              onclick: () => {
                if (editing) void retargetIssue(editing);
              },
            },
            `Update work item #${filedIssue.id}`,
          ),
        ),
      );
    }

    // The lists are a CONVENIENCE, never a gate. They used to REPLACE the
    // fields with a "Loading…" line, so a slow tracker (this is four ADO calls)
    // hid the filing controls at exactly the moment a tester reached for them.
    // The same fields take a typed id, so the wait is annotated, not enforced.
    if (state.issueTargetsStatus !== "loaded") {
      fields.append(
        h(
          "p",
          { class: "note" },
          "Loading people and sprints… type an id meanwhile, or leave a field blank for the tracker's own default.",
        ),
      );
    }

    syncFields();
  }

  const wordingNote =
    editing && !isAuthor
      ? h(
          "p",
          { class: "note" },
          `Written by ${editing.session.tester} — only they can change the wording. Severity and the comment are yours.`,
        )
      : null;

  // Fetched only when asked for: most edits are not a question about what the
  // finding used to say, and the revisions are a second round-trip.
  let history: Node | null = null;
  if (editing?.edited_at) {
    const id = editing.id;
    const box = h(
      "div",
      { style: "margin-top:8px" },
      h("label", {}, "Edit history"),
    );
    const loaded = state.revisions.get(id);
    if (!loaded) {
      box.append(
        h(
          "button",
          {
            class: "btn",
            type: "button",
            "data-ref": "QaDeck:Finding:history",
            onclick: async () => {
              try {
                const { revisions } = await api.getFindingRevisions(id);
                state.revisions.set(id, revisions);
              } catch {
                state.revisions.set(id, []);
              }
              render();
            },
          },
          "Show earlier wording",
        ),
      );
    } else if (!loaded.length) {
      box.append(h("p", { class: "note" }, "None."));
    } else {
      for (const revision of loaded) {
        box.append(
          h(
            "div",
            { class: "ref", style: "margin-top:6px" },
            `${revision.at.slice(0, 16).replace("T", " ")} · ${revision.actor}`,
          ),
          h("div", { class: "note" }, revision.observed),
        );
      }
    }
    box.append(
      h(
        "p",
        { class: "note" },
        `Edited ${editing.edited_at.slice(0, 16).replace("T", " ")}.`,
      ),
    );
    history = box;
  }

  return h(
    "details",
    { class: "group", open: "open" },
    h("summary", {}, editing ? "Edit finding" : "New finding"),
    h(
      "div",
      { style: "padding:8px 10px" },
      h("label", {}, "Severity"),
      severity,
      h("label", {}, "Observed"),
      observed,
      h("label", {}, "Expected"),
      expected,
      wordingNote,
      h("label", {}, "Comment"),
      comment,
      // Images, the picked element and the step marks are filed evidence with
      // no patch shape. They stay on the card, read-only, rather than sitting
      // in a form whose Save could not carry them.
      editing ? null : h("label", {}, "Images"),
      editing
        ? null
        : renderAttachments({
            refPrefix: "QaDeck:Finding",
            ids: draft.attachments,
            onChange: onImages,
          }),
      editing ? null : steps,
      editing ? null : linked,
      tracker,
      history,
      h(
        "div",
        { style: "margin-top:8px; display:flex; flex-wrap:wrap; gap:6px" },
        editing
          ? h(
              "button",
              {
                class: "btn",
                type: "button",
                "data-ref": "QaDeck:Finding:edit-cancel",
                onclick: () => cancelEditing(),
              },
              "Cancel",
            )
          : // With several picked, a single pick would quietly throw the
            // others away; the chips' ✕ and "Pick more" edit the list instead.
            state.picks.length > 1
            ? null
            : pickButton,
        editing ? null : pickManyButton,
        h(
          "button",
          {
            class: "btn primary",
            type: "button",
            "data-ref": editing
              ? "QaDeck:Finding:edit-save"
              : "QaDeck:Finding:submit",
            onclick: () =>
              void (editing ? saveFindingEdits(editing) : submitFinding()),
          },
          editing ? "Save changes" : "Save finding",
        ),
      ),
      editing ? null : pickInfo,
    ),
  );
}

/**
 * "This finding is not about that scenario after all."
 *
 * Without it, ✕ on a checklist row was a one-way door: `linkedItemId` cleared
 * only on save, so every LATER finding written on the page — a free-standing
 * one about something else entirely — was still filed against the last scenario
 * anyone failed, carrying its steps with it.
 *
 * The tester's own words are left exactly where they are. Only the link and the
 * steps that belong to it go, because those are the two things that stop being
 * true the moment the finding is about something else.
 */
function unlinkDraft(): void {
  state.linkedItemId = null;
  draft.stepResults = [];
}

/**
 * The linked scenario's steps, each with a ✓/✕ pair — the form's answer to
 * "how far did you get before it broke?".
 *
 * Same visual language as the scenario's own verdict trio, and mutually
 * exclusive by construction rather than by validation. UNMARKED is the third
 * state and the important one: when step 3 fails, steps 4–8 usually never ran,
 * and a UI that demanded an answer for them would collect fiction. Pressing the
 * mark a step already has takes it back off.
 *
 * Absent entirely for a free-standing finding and for a scenario with no steps
 * — there is nothing to mark, and an empty heading reads like a bug.
 */
function renderDraftSteps(): Node | null {
  if (!draft.stepResults.length) return null;

  const listEl = h("ol", { class: "step-results" });
  draft.stepResults.forEach((step, index) => {
    const mark = (status: StepResultStatus, glyph: string, title: string) =>
      h(
        "button",
        {
          class: `btn${step.status === status ? " active" : ""}`,
          type: "button",
          title,
          "data-ref": `QaDeck:Finding:step-${index}-${status}`,
          onclick: () => {
            step.status = step.status === status ? null : status;
            // Keeps the caret and the scroll position: the tester is usually
            // part-way through the observed box when they mark a step.
            renderKeepingFocus();
          },
        },
        glyph,
      );

    listEl.append(
      h(
        "li",
        { "data-status": step.status ?? "unreached" },
        h("span", { class: "step-text" }, step.text),
        h(
          "span",
          { class: "statuses" },
          mark("passed", "✓", "This step worked"),
          mark("failed", "✕", "This step is where it went wrong"),
        ),
      ),
    );
  });

  return h(
    "div",
    {},
    h("label", {}, "Steps"),
    listEl,
    h(
      "p",
      { class: "note" },
      "Leave a step unmarked if you never got that far — that is recorded as “not reached”.",
    ),
  );
}

async function submitFinding(): Promise<void> {
  if (!state.session) return;
  if (!draft.observed.trim()) {
    state.error = "Observed is required.";
    render();
    return;
  }

  const { environment, openreplay_url } = await captureContext();
  const body = {
    session_id: state.session.id,
    route_key: state.routeKey,
    url: location.href,
    checklist_item_id: state.linkedItemId,
    severity: draft.severity,
    observed: draft.observed.trim(),
    expected: draft.expected.trim() || null,
    comment: draft.comment.trim() || null,
    anchor: state.picks[0]?.anchor ?? null,
    element_context: state.picks[0]?.elementContext ?? null,
    // Only when there are some: a server older than the field would drop it
    // in silence, and the button that makes them is hidden from one anyway.
    ...(state.picks.length > 1
      ? {
          more_elements: state.picks.slice(1).map((pick) => ({
            anchor: pick.anchor,
            element_context: pick.elementContext,
          })),
        }
      : {}),
    attachment_ids: draft.attachments,
    // The words as this tester read them, not indices into a spec that a
    // re-ingest will rewrite — see `FindingStepResult`.
    step_results: draft.stepResults,
    console_tail: getConsoleTail(),
    environment,
    openreplay_url,
    // Idempotency key for the offline outbox — a retry can never duplicate.
    client_id: `c_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`,
  };

  try {
    const created = await api.createFinding(body);
    draft.observed = "";
    draft.expected = "";
    draft.comment = "";
    draft.attachments = [];
    draft.stepResults = [];
    state.picks = [];
    state.linkedItemId = null;
    state.error = null;
    flashStatus(created ? "saved" : "queued offline");
    if (created) state.findings = [created, ...state.findings];
    else state.outbox = api.outboxSize();

    // Only a finding that actually reached the server can be filed. A queued
    // one has no id yet, and inventing one would file a bug that does not exist.
    if (created && draft.fileToTracker) {
      try {
        const result = await api.fileIssue(created.id, {
          assignee: state.target.assignee || null,
          iteration: state.target.iteration || null,
          parentId: state.target.parentId || null,
          workItemType: state.target.workItemType || null,
        });
        replaceFinding(result.finding);
        flashStatus(`saved · filed as #${result.issue.id}`);
      } catch (error) {
        // The finding is saved either way — say the filing failed rather than
        // implying the tester's report was lost.
        state.error =
          error instanceof Error
            ? `Saved, but filing failed: ${error.message}`
            : "Saved, but filing failed";
      }
    }
    draft.fileToTracker = false;
    render();
  } catch (error) {
    state.error =
      error instanceof Error ? error.message : "Could not save finding";
    render();
  }
}

/** ✓ / ✕ / — for a filed finding's steps. The dash is "not reached", which is
 *  a fact about the run and not a missing answer, so it is shown, never
 *  omitted. */
const STEP_MARKS: Record<string, string> = {
  passed: "✓",
  failed: "✕",
  unreached: "—",
};

function renderFindingSteps(finding: Finding): Node | null {
  const steps = wireList(finding.step_results);
  if (!steps.length) return null;
  const listEl = h("ol", { class: "step-results done" });
  steps.forEach((step, index) => {
    const key = step.status ?? "unreached";
    listEl.append(
      h(
        "li",
        {
          "data-status": key,
          "data-ref": `QaDeck:Finding:${finding.id}-step-${index}`,
        },
        h("span", { class: "mark" }, STEP_MARKS[key]),
        h("span", { class: "step-text" }, step.text),
      ),
    );
  });
  return listEl;
}

/**
 * A finding to open as soon as the first read lands — set by arriving on a page
 * through "Retest it", cleared the moment it is used.
 */
let pendingFocus: api.FindingFocus | null = null;
/** A held retest pin found at boot — restored quietly after the first read. */
let restoredFocus: api.FindingFocus | null = null;

/** Is this the page that pointer was addressed to? Path only: a query string
 *  is a sub-view of the same page, and the finding is listed page-wide. */
const ANSWER_REF = "QaDeck:Answer";

/**
 * What the tester is composing in answer to a fix claim, keyed by finding id.
 *
 * Module-level like `commentDrafts`, and for the same reason: the panel rebuilds
 * its whole body on every state change, and a draft held in the DOM would be
 * lost every time the inbox poll ticked.
 */
interface AnswerDraft {
  note: string;
  attachments: string[];
  sending: boolean;
  error: string | null;
}
const answerDrafts = new Map<string, AnswerDraft>();

function answerDraft(findingId: string): AnswerDraft {
  const existing = answerDrafts.get(findingId);
  if (existing) return existing;
  const fresh: AnswerDraft = {
    note: "",
    attachments: [],
    sending: false,
    error: null,
  };
  answerDrafts.set(findingId, fresh);
  return fresh;
}

/**
 * "Answer the claim" — the one job a `fixed` finding gives a tester.
 *
 * At the TOP of the card, because it is what the card is for once somebody has
 * claimed a fix; the evidence that produced it stays below, unchanged. Three
 * things in one block, which is what makes it an answer rather than a click:
 *
 *  - **which build the fix is in**, held against the one on screen — the
 *    difference between "retest this" and "this is not here yet";
 *  - **the verdict**, the two words that settle it;
 *  - **a note and a screenshot**, optional, sent WITH the verdict as one
 *    comment in the thread the developer is already reading.
 *
 * The screenshot lands on the MESSAGE, never on the finding: a finding's own
 * evidence is frozen at filing (rule 9a), and "still broken, look" is a new
 * observation about a claim rather than a correction of the original report.
 */
function renderAnswerBlock(finding: Finding): Node {
  const draft = answerDraft(finding.id);
  const wrap = h("div", {
    class: "answer",
    "data-ref": `${ANSWER_REF}:block-${finding.id}`,
  });

  wrap.append(h("label", {}, "Answer the claim"));

  const claimedAt = finding.fixed_at
    ? ` · claimed ${finding.fixed_at.slice(0, 10)}`
    : "";
  wrap.append(
    h(
      "div",
      { class: "note" },
      `A fix was claimed on this finding${claimedAt}.`,
    ),
  );
  // Which build it landed in, against the build this page is running. Silent
  // when either side has no stamp — see `buildNote`.
  const build = buildNote(
    finding.fixed_in_build ?? null,
    finding.environment?.build?.sha ?? null,
  );
  if (build) wrap.append(build);
  // A claim made on a live preview names where to try it (§7k) — the page
  // this finding was filed on still runs the old code.
  const preview = finding.retest_url ?? null;
  if (preview && !isSameOrigin(preview)) {
    wrap.append(
      h(
        "div",
        { class: "note", "data-ref": `${ANSWER_REF}:preview-${finding.id}` },
        "Try it on the live preview: ",
        inboxLink(preview, "Open the live preview of the fix"),
      ),
    );
  }

  const verdicts = h("div", { class: "row", style: "margin-top:6px" });
  verdicts.append(
    h(
      "button",
      {
        class: "btn primary",
        type: "button",
        title: "The fix works — this finding is resolved",
        "data-ref": `${ANSWER_REF}:held-${finding.id}`,
        onclick: () => void sendAnswer(finding, "resolved"),
      },
      "✓ It held",
    ),
    h(
      "button",
      {
        class: "btn danger",
        type: "button",
        title: "Still broken — this finding goes back to open",
        "data-ref": `${ANSWER_REF}:broken-${finding.id}`,
        onclick: () => void sendAnswer(finding, "open"),
      },
      "✕ Still broken",
    ),
  );
  for (const button of Array.from(verdicts.querySelectorAll("button"))) {
    if (draft.sending) button.disabled = true;
  }
  wrap.append(verdicts);

  const note = h("textarea", {
    placeholder: "What you saw (optional) — sent with your answer",
    "data-ref": `${ANSWER_REF}:note-${finding.id}`,
  }) as HTMLTextAreaElement;
  note.value = draft.note;
  note.addEventListener("input", () => {
    draft.note = note.value;
  });
  // Grab region, click in the box, paste — the way a screenshot actually
  // reaches a bug report.
  enableImagePaste(
    note,
    () => draft.attachments,
    (ids) => {
      draft.attachments = ids;
      renderKeepingFocus();
    },
  );
  wrap.append(note);

  wrap.append(
    renderAttachments({
      refPrefix: `${ANSWER_REF}:${finding.id}`,
      ids: draft.attachments,
      onChange: (ids) => {
        draft.attachments = ids;
        renderKeepingFocus();
      },
    }),
  );

  if (draft.error) wrap.append(h("div", { class: "error" }, draft.error));
  return wrap;
}

/**
 * Send the verdict, with whatever evidence came with it.
 *
 * The COMMENT goes first and the status second, deliberately. A status write
 * that lands while the note does not leaves a finding settled with no reason
 * on it — the exact hole `fix_note` had in the other direction. The other order
 * costs nothing: if the status write fails, the words and the screenshot are
 * already in the thread and the verdict can be pressed again.
 */
async function sendAnswer(
  finding: Finding,
  status: "resolved" | "open",
): Promise<void> {
  const draft = answerDraft(finding.id);
  if (draft.sending) return;
  draft.sending = true;
  draft.error = null;
  render();

  const note = draft.note.trim();
  try {
    if (note || draft.attachments.length) {
      const body =
        note ||
        (status === "resolved"
          ? "Verified fixed."
          : "Still broken — see the screenshot.");
      const result = await api.addFindingComment(
        finding.id,
        body,
        state.session
          ? { sessionId: state.session.id, attachmentIds: draft.attachments }
          : undefined,
      );
      // Keep an open thread in step rather than making the tester re-open it
      // to see what they just said.
      const view = state.threads.get(finding.id);
      if (view) view.comments = [...view.comments, result.comment];
    }
    // The same door as "Change status": guarded on the claim still standing,
    // and said in the thread, so the developer reads who verified it.
    const { finding: updated } = await api.setTesterStatus(
      finding.id,
      "fixed",
      status,
    );
    replaceFinding(updated);
    answerDrafts.delete(finding.id);
    clearInboxRetest(finding.id);
    // Release 1 of 3: the verdict is the job finishing. The pin goes, and so
    // does the pinned card — it was there to collect this answer, and the
    // finding it showed is another page's; the flash says what happened.
    api.releaseFindingFocus(finding.id);
    if (state.focus?.id === finding.id) state.focus = null;
    flashStatus(status === "resolved" ? "✓ verified fixed" : "✕ still broken");
  } catch (error) {
    draft.sending = false;
    draft.error =
      error instanceof Error ? error.message : "Could not send that answer";
  } finally {
    draft.sending = false;
    render();
  }
}

const DISCUSS_REF = "QaDeck:Discuss";

/**
 * "Your view is needed" — the job a `needs_discussion` finding gives a tester.
 *
 * A QUESTION, not a claim: nothing was fixed, so there is nothing to retest.
 * The developer proposed something and wants the tester's view before changing
 * anything. Two ways to answer, both here:
 *
 *  - **a verdict** — go ahead (decided work goes back to the developer) or not
 *    needed (won't fix) — with an optional note and screenshot;
 *  - **a reply** in the conversation below, which keeps the question open for
 *    as long as the two of them need.
 *
 * The question itself is shown when the inbox carries it, so the block is
 * readable without opening the thread; the thread has the whole exchange.
 */
function renderDiscussionBlock(finding: Finding): Node {
  const draft = answerDraft(finding.id);
  const wrap = h("div", {
    class: "answer discuss",
    "data-ref": `${DISCUSS_REF}:block-${finding.id}`,
  });
  wrap.append(h("label", {}, "Your view is needed"));

  // The finding carries its own question; the inbox row is only a fallback
  // for a server older than that field. A snoozed question leaves the inbox,
  // and the card then asked for a view on something it did not show.
  const asked = wireList(state.inbox?.discussions).find(
    (row) => row.finding_id === finding.id,
  );
  const question = finding.discussion_question ?? asked?.question ?? null;
  const askedBy = finding.discussion_asked_by ?? asked?.asked_by ?? null;
  wrap.append(
    h(
      "div",
      { class: "note" },
      `${askedBy ?? "A developer"} wants your view before changing anything${
        finding.discussion_at
          ? ` · asked ${finding.discussion_at.slice(0, 10)}`
          : ""
      }.`,
    ),
  );
  if (question) {
    wrap.append(h("div", { class: "question" }, question));
  }

  const verdicts = h("div", { class: "row", style: "margin-top:6px" });
  verdicts.append(
    h(
      "button",
      {
        class: "btn primary",
        type: "button",
        title: "Agree — the developer goes ahead",
        "data-ref": `${DISCUSS_REF}:agree-${finding.id}`,
        onclick: () => void sendDiscussionAnswer(finding, "agree"),
      },
      "✓ Go ahead",
    ),
    h(
      "button",
      {
        class: "btn",
        type: "button",
        title: "Not needed — the finding is closed as won't fix",
        "data-ref": `${DISCUSS_REF}:drop-${finding.id}`,
        onclick: () => void sendDiscussionAnswer(finding, "drop"),
      },
      "✕ Not needed",
    ),
  );
  for (const button of Array.from(verdicts.querySelectorAll("button"))) {
    if (draft.sending) button.disabled = true;
  }
  wrap.append(verdicts);

  const note = h("textarea", {
    placeholder: "Why (optional) — sent with your answer",
    "data-ref": `${DISCUSS_REF}:note-${finding.id}`,
  }) as HTMLTextAreaElement;
  note.value = draft.note;
  note.addEventListener("input", () => {
    draft.note = note.value;
  });
  enableImagePaste(
    note,
    () => draft.attachments,
    (ids) => {
      draft.attachments = ids;
      renderKeepingFocus();
    },
  );
  wrap.append(note);
  wrap.append(
    renderAttachments({
      refPrefix: `${DISCUSS_REF}:${finding.id}`,
      ids: draft.attachments,
      onChange: (ids) => {
        draft.attachments = ids;
        renderKeepingFocus();
      },
    }),
  );
  wrap.append(
    h(
      "div",
      { class: "note", style: "margin-top:4px" },
      "Not sure yet? Reply in the conversation below — the question stays open.",
    ),
  );

  if (draft.error) wrap.append(h("div", { class: "error" }, draft.error));
  return wrap;
}

/**
 * Send the verdict on a question. One request: the server writes the tester's
 * words, a line naming the verdict and the new status together, so there is no
 * order to get wrong here — and a question answered elsewhere meanwhile comes
 * back as an error on the block rather than a second, conflicting answer.
 */
async function sendDiscussionAnswer(
  finding: Finding,
  verdict: "agree" | "drop",
): Promise<void> {
  const draft = answerDraft(finding.id);
  if (draft.sending) return;
  draft.sending = true;
  draft.error = null;
  render();

  try {
    const { finding: updated } = await api.answerDiscussion(
      finding.id,
      verdict,
      draft.note.trim(),
      state.session
        ? { sessionId: state.session.id, attachmentIds: draft.attachments }
        : undefined,
    );
    replaceFinding(updated);
    answerDrafts.delete(finding.id);
    clearInboxDiscussion(finding.id);
    // The question was an unread message too; answering it is reading it (the
    // server stamped the mark), so it must not linger as a "replied" row.
    clearInboxReply(finding.id);
    // An open thread gets the words and the verdict line it just gained.
    const view = state.threads.get(finding.id);
    if (view) {
      const { comments } = await api.listFindingComments(finding.id);
      view.comments = wireList(comments);
    }
    flashStatus(verdict === "agree" ? "✓ go ahead" : "✕ not needed");
  } catch (error) {
    draft.error =
      error instanceof Error ? error.message : "Could not send that answer";
  } finally {
    draft.sending = false;
    render();
  }
}

const THREAD_REF = "QaDeck:Thread";
const ASK_REF = "QaDeck:Ask";
/** Findings with an answer in flight from the ask block — one at a time. */
const askBusy = new Set<string>();
/** The last send that failed, per finding, until the next attempt. */
const askErrors = new Map<string, string>();

/**
 * "Your turn" — the message the tester owes an answer to, at the top of the
 * card, with the box to answer it already open.
 *
 * First on the card for the same reason a fix claim's answer block is: it is
 * what the card is FOR right now, and the report below it is the tester's own
 * words, which they already know. The draft is the thread's (`commentDrafts`),
 * so words started here are still there if the conversation is opened.
 */
function renderAskBlock(finding: Finding): Node | null {
  const waiting = finding.awaiting_tester ?? null;
  if (!waiting) return null;
  const busy = askBusy.has(finding.id);
  const wrap = h("div", {
    class: "answer ask",
    "data-ref": `${ASK_REF}:block-${finding.id}`,
  });
  wrap.append(
    h(
      "label",
      {},
      `Your turn · ${waiting.author} asked ${sinceLabel(waiting.at)}`,
    ),
    h("div", { class: "question" }, waiting.body),
  );
  const input = h("textarea", {
    placeholder: "Your answer…",
    "data-ref": `${ASK_REF}:input-${finding.id}`,
  }) as HTMLTextAreaElement;
  input.value = commentDrafts.get(finding.id) ?? "";
  input.addEventListener("input", () => {
    commentDrafts.set(finding.id, input.value);
  });
  // Enter sends, Shift+Enter breaks the line — as in the thread below.
  input.addEventListener("keydown", (event) => {
    const key = event as KeyboardEvent;
    if (key.key === "Enter" && !key.shiftKey) {
      key.preventDefault();
      void sendAsk(finding);
    }
  });
  wrap.append(input);
  wrap.append(
    h(
      "div",
      { class: "row", style: "margin-top:6px" },
      h(
        "button",
        {
          class: "btn primary",
          type: "button",
          "data-ref": `${ASK_REF}:send-${finding.id}`,
          disabled: busy,
          onclick: () => void sendAsk(finding),
        },
        busy ? "Sending…" : "Send",
      ),
      h("span", { class: "note" }, "Settled instead? Change status below."),
    ),
  );
  const error = askErrors.get(finding.id);
  if (error) wrap.append(h("div", { class: "error" }, error));
  return wrap;
}

/**
 * Send the answer from the ask block. The same comment the thread posts —
 * this is only a closer door to it — so the turn passes the same way: the
 * server returns the finding with nobody waiting, and the block goes.
 */
async function sendAsk(finding: Finding): Promise<void> {
  if (askBusy.has(finding.id)) return;
  const body = (commentDrafts.get(finding.id) ?? "").trim();
  if (!body) return;
  const waitingOn = finding.awaiting_tester?.author ?? "the team";
  askBusy.add(finding.id);
  askErrors.delete(finding.id);
  render();
  try {
    const result = await api.addFindingComment(finding.id, body);
    commentDrafts.delete(finding.id);
    const view = state.threads.get(finding.id);
    if (view) view.comments = [...view.comments, result.comment];
    if (result.finding) replaceFinding(result.finding);
    clearInboxAnswer(finding.id);
    clearInboxReply(finding.id);
    flashStatus(`Sent — waiting on ${waitingOn}`);
  } catch (error) {
    // Rule 8 stops at messages: the words stay in the box for the retry.
    askErrors.set(
      finding.id,
      error instanceof Error ? error.message : "Could not send that answer",
    );
  } finally {
    askBusy.delete(finding.id);
    renderKeepingFocus();
  }
}

/**
 * Open or close one finding's conversation.
 *
 * Fetched on open, never with the list: a route can hold a dozen findings and
 * most have nothing said about them, so the COUNT rides along on the finding
 * (computed server-side) and the words are paid for when someone asks.
 */
async function toggleThread(finding: Finding): Promise<void> {
  if (state.threads.has(finding.id)) {
    state.threads.delete(finding.id);
    render();
    return;
  }

  const view: ThreadView = {
    comments: [],
    loading: true,
    sending: false,
    error: null,
  };
  state.threads.set(finding.id, view);
  render();

  try {
    const { comments } = await api.listFindingComments(finding.id);
    view.comments = wireList(comments);
    // The read stamped the mark server-side; drop the row here too so opening
    // a conversation visibly clears the strip that pointed at it.
    clearInboxReply(finding.id);
  } catch (error) {
    // A thread that would not load is no reason to refuse the reply — writing
    // still works, it just starts without the earlier turns on screen.
    view.error =
      error instanceof Error
        ? `Could not load the conversation: ${error.message}`
        : "Could not load the conversation";
  } finally {
    view.loading = false;
    if (state.threads.get(finding.id) === view) renderKeepingFocus();
  }
}

/**
 * Say something back.
 *
 * The draft is cleared ONLY on success, and nothing is queued: rule 8 covers
 * EVIDENCE, and a comment is a message — the same boundary item chat draws. A
 * failed send leaves the words in the box for the retry.
 */
async function sendComment(finding: Finding): Promise<void> {
  const view = state.threads.get(finding.id);
  if (!view || view.sending) return;
  const body = (commentDrafts.get(finding.id) ?? "").trim();
  if (!body) return;

  view.sending = true;
  view.error = null;
  render();

  try {
    const result = await api.addFindingComment(finding.id, body);
    view.comments = [...view.comments, result.comment];
    commentDrafts.delete(finding.id);
    // Saying something IS taking the turn.
    clearInboxAnswer(finding.id);
    // Keep the card's own count in step without a full refresh, which would
    // rebuild the finding form under whoever is writing in it.
    if (result.finding) replaceFinding(result.finding);
  } catch (error) {
    view.error =
      error instanceof Error ? error.message : "Could not post that comment";
  } finally {
    view.sending = false;
    renderKeepingFocus();
  }
}

/** Who said it, in the words a tester reads on the card. */
const COMMENT_WHO: Record<string, string> = {
  developer: "developer",
  tester: "you",
  agent: "agent",
  system: "system",
};

/**
 * The conversation on one finding, under its card.
 *
 * Collapsed until asked for. The button says how many comments there are,
 * because "Conversation" with nothing behind it is a click the tester pays for
 * to learn nothing — the count comes off the finding itself, read through `??`
 * because a server older than this bundle never sends it.
 */
function renderFindingThread(finding: Finding): Node {
  const view = state.threads.get(finding.id) ?? null;
  const count = finding.comments_count ?? 0;
  const unread = wireList(state.inbox?.replies).find(
    (row) => row.finding_id === finding.id,
  );

  const wrap = h("div", { class: "thread" });
  wrap.append(
    h(
      "button",
      {
        class: unread ? "btn primary" : "btn",
        type: "button",
        "data-ref": `${THREAD_REF}:open-${finding.id}`,
        onclick: () => void toggleThread(finding),
      },
      view
        ? "Hide conversation"
        : unread
          ? `Conversation (${count}) · ${unread.unread} new`
          : count
            ? `Conversation (${count})`
            : "Comment",
    ),
  );
  if (!view) return wrap;

  if (view.error) wrap.append(h("div", { class: "error" }, view.error));

  const log = h("div", {
    class: "chat-log",
    "data-ref": `${THREAD_REF}:log-${finding.id}`,
  });
  if (view.loading) {
    log.append(h("p", { class: "note" }, "Loading…"));
  } else if (!view.comments.length) {
    log.append(
      h(
        "p",
        { class: "note" },
        "Nothing said yet. Anything you write here reaches the team on the board.",
      ),
    );
  } else {
    for (const comment of view.comments) {
      const images = wireList(comment.attachments);
      log.append(
        h(
          "div",
          { class: `chat-msg ${comment.author_kind}` },
          h(
            "div",
            { class: "chat-who" },
            `${comment.author} · ${COMMENT_WHO[comment.author_kind] ?? comment.author_kind}`,
          ),
          h("div", { class: "chat-text" }, comment.body),
          // Evidence attached to THIS message. Read-only, like the finding's
          // own: a message is append-only, so there is no shape that could add
          // or drop one after the fact.
          images.length
            ? h(
                "div",
                { class: "thumbs" },
                ...images.map((id) =>
                  imageThumb(id, `${THREAD_REF}:${comment.id}`, null),
                ),
              )
            : null,
        ),
      );
    }
  }
  wrap.append(log);

  const input = h("textarea", {
    placeholder: "Answer, or add what you saw…",
    "data-ref": `${THREAD_REF}:input-${finding.id}`,
  }) as HTMLTextAreaElement;
  input.value = commentDrafts.get(finding.id) ?? "";
  input.addEventListener("input", () => {
    commentDrafts.set(finding.id, input.value);
  });
  // Enter sends, Shift+Enter breaks the line — the same as the scenario chat,
  // because it is the same gesture in the same panel.
  input.addEventListener("keydown", (event) => {
    const key = event as KeyboardEvent;
    if (key.key === "Enter" && !key.shiftKey) {
      key.preventDefault();
      void sendComment(finding);
    }
  });
  wrap.append(input);

  wrap.append(
    h(
      "div",
      { style: "margin-top:6px" },
      h(
        "button",
        {
          class: "btn primary",
          type: "button",
          "data-ref": `${THREAD_REF}:send-${finding.id}`,
          disabled: view.sending,
          onclick: () => void sendComment(finding),
        },
        view.sending ? "Sending…" : "Reply",
      ),
    ),
  );

  return wrap;
}

function renderFinding(finding: Finding): Node {
  // What a tester says when they walk over and mention this one. Leads the
  // row, and is simply absent when the server answering is older than the
  // column — the widget is routinely the newer half.
  const ref = formatFindingRef(finding.ref);
  const tags = h(
    "div",
    { class: "row" },
    ref ? h("span", { class: "tag ref" }, ref) : null,
    h("span", { class: `tag ${finding.severity}` }, finding.severity),
    h(
      "span",
      { class: `tag status-${finding.status}` },
      finding.status.replace(/_/g, " "),
    ),
    finding.classification
      ? h("span", { class: "tag" }, finding.classification)
      : null,
    finding.issue
      ? h(
          "span",
          { class: "tag" },
          `${finding.issue.tracker} #${finding.issue.id}`,
        )
      : null,
  );

  const actions = h("div", { class: "row", style: "margin-top:6px" });
  // A `fixed` finding's verdict is NOT here — it is the answer block at the top
  // of the card, which carries the note and the screenshot with it. Two places
  // to answer the same question is how one of them ends up carrying no
  // evidence.
  //
  // Every OTHER status move is the one control below, on every card whatever
  // its status: a verdict given by mistake is always the tester's to take back.
  actions.append(renderStatusControl(finding));
  const aiFix = renderAiFixControl(finding);
  if (aiFix) actions.append(aiFix);
  // Always present — a tracker is not what makes a finding editable. Pressing
  // it lifts this finding into the form at the TOP of the tab, pre-filled: the
  // same form it was written in, so there is one place to edit and one Save.
  //
  // While an edit is open, no card offers one. THIS card is inert and dimmed
  // (`.finding.editing`) because its buttons would act on a finding the tester
  // is in the middle of rewriting; every OTHER card's Edit is disabled because
  // it would swap the form's contents out and lose the unsaved edit without
  // saying so. Save changes and Cancel, up in the form, are the way out.
  const editingThis = state.editingFindingId === finding.id;
  const editButton = h(
    "button",
    {
      class: "btn",
      type: "button",
      "data-ref": `QaDeck:Finding:edit-${finding.id}`,
      title: state.editingFindingId
        ? "Save or cancel the edit in the form above first"
        : "Edit this finding in the form above",
      onclick: () => startEditingFinding(finding),
    },
    "Edit",
  );
  if (state.editingFindingId) editButton.disabled = true;
  actions.append(editButton);

  const el = h(
    "div",
    {
      class: `finding${editingThis ? " editing" : ""}`,
      "data-finding-id": finding.id,
    },
    // First, because it is what the card is FOR once a fix has been claimed:
    // the evidence that produced it is unchanged and reads below.
    finding.status === "fixed" ? renderAnswerBlock(finding) : null,
    finding.status === "needs_discussion"
      ? renderDiscussionBlock(finding)
      : null,
    turnOf(finding) === "answer" ? renderAskBlock(finding) : null,
    tags,
    h("div", { class: "observed" }, finding.observed),
    // Read-only, like the words above it: how the steps went is the tester's
    // evidence, recorded once at filing (rule 2).
    renderFindingSteps(finding),
    // What the fixer says changed, so the retest knows what to look for. Read
    // through `??`: a server older than this widget never sends the field.
    (finding.fix_note ?? null)
      ? h(
          "div",
          {
            class: "fix-note",
            "data-ref": `QaDeck:Finding:fix-note-${finding.id}`,
          },
          `Fix: ${finding.fix_note}`,
        )
      : null,

    finding.anchor
      ? h(
          "div",
          { class: "ref" },
          finding.anchor.anchorRef ?? finding.anchor.selector,
          wireList(finding.more_elements).length
            ? ` + ${wireList(finding.more_elements).length} more`
            : "",
        )
      : null,
    // Read-only here: a finding's evidence is fixed once filed, and there is no
    // PATCH shape that could add or drop one.
    wireList(finding.attachments).length
      ? h(
          "div",
          { class: "thumbs" },
          ...wireList(finding.attachments).map((id) =>
            imageThumb(id, `QaDeck:Finding:${finding.id}`, null),
          ),
        )
      : null,
    h(
      "div",
      { class: "note" },
      `${finding.session.tester} · ${finding.created_at.slice(0, 16).replace("T", " ")}`,
    ),
    actions,
    // The human channel `fix_note` could not be: a developer asks about what
    // was written here, and the answer belongs on the same card.
    renderFindingThread(finding),
  );

  // Not in the edit form: this is a fact about the finding as it stands, and
  // the tester needs to read it before deciding to open anything.
  if (finding.issue_stale) {
    el.append(
      h(
        "div",
        { class: "error", "data-ref": `QaDeck:Finding:stale-${finding.id}` },
        `The wording changed after #${finding.issue?.id} was created, so the work item no longer matches.`,
      ),
    );
  }
  return el;
}

/**
 * Load the pickable targets once per panel session, then open the form.
 * Pre-filled from what this project filed against last — the sprint and story
 * barely change between bugs in one sweep.
 */
/** The tracker as a human names it, not the adapter id. */
function trackerLabel(): string {
  return state.config?.issueTracker === "ado"
    ? "Azure DevOps"
    : (state.config?.issueTracker ?? "the issue tracker");
}

async function ensureIssueTargets(): Promise<void> {
  if (state.issueTargetsStatus !== "idle") return;
  state.issueTargetsStatus = "loading";
  try {
    const result = await api.getIssueTargets();
    state.issueTargets = result.targets;
    state.target = {
      assignee: result.defaults.assignee ?? "",
      iteration: result.defaults.iteration ?? "",
      parentId: result.defaults.parentId ?? "",
      workItemType: result.defaults.workItemType ?? "",
    };
  } catch {
    // A tracker that cannot enumerate is not a tracker that cannot file: the
    // form falls back to free-text ids, which is a real path, not a broken one.
    state.issueTargets = null;
  }
  // Set in the same tick as the render below, so nothing ever renders the
  // fallback fields while the answer is still on its way.
  state.issueTargetsStatus = "loaded";
  render();
}

/**
 * Lift a filed finding into the form at the top of the tab.
 *
 * Only the fields that can actually be written back are loaded: severity and
 * the comment (`patchFinding`), and the wording (`correctFinding`, author
 * only). Attachments, the anchor and the step results are filed evidence with
 * no patch shape — they stay on the card, read-only, rather than appearing in
 * a form that could not save them.
 */
function startEditingFinding(finding: Finding): void {
  // Park an unfinished new finding rather than overwrite it.
  if (!state.editingFindingId) {
    parkedDraft = {
      draft: { ...draft },
      picks: state.picks,
      linkedItemId: state.linkedItemId,
    };
  }
  state.editingFindingId = finding.id;
  // The form lives at the top of "This page"; an edit started from "All
  // findings" must open where the form is, or it opened nowhere visible.
  state.tab = "findings";
  draft.severity = finding.severity;
  draft.observed = finding.observed;
  draft.expected = finding.expected ?? "";
  draft.comment = finding.comment ?? "";
  draft.attachments = [];
  draft.stepResults = [];
  draft.fileToTracker = false;
  state.picks = [];
  state.linkedItemId = null;
  state.newStory = "";
  state.error = null;
  if (isPicking()) stopPick();
  render();
  // "Open it up above" literally: the form is at the top of a scrolled list,
  // and an edit the tester cannot see has not opened.
  bodyEl.scrollTop = 0;
  if (state.config?.issueTrackerConfigured) void ensureIssueTargets();
}

/** Leave edit mode and put any parked new finding back in the form. */
function finishEditing(): void {
  state.editingFindingId = null;
  if (parkedDraft) {
    Object.assign(draft, parkedDraft.draft);
    state.picks = parkedDraft.picks;
    state.linkedItemId = parkedDraft.linkedItemId;
    parkedDraft = null;
  } else {
    draft.observed = "";
    draft.expected = "";
    draft.comment = "";
    draft.fileToTracker = false;
  }
  render();
}

function cancelEditing(): void {
  state.error = null;
  finishEditing();
}

/** The four tracker fields as one value — read at save time, never earlier. */
function targetSelection(): api.IssueTargetSelection {
  return {
    assignee: state.target.assignee || null,
    iteration: state.target.iteration || null,
    parentId: state.target.parentId || null,
    workItemType: state.target.workItemType || null,
  };
}

/** A select when the tracker could enumerate, a text box when it could not. */
function targetField(
  label: string,
  refId: string,
  options: api.IssueTargetOption[] | undefined,
  value: string,
  placeholder: string,
  onChange: (value: string) => void,
): Node {
  const wrap = h("div", { style: "margin-bottom:6px" }, h("label", {}, label));
  if (options?.length) {
    const select = h(
      "select",
      {
        "data-ref": refId,
        onchange: (event: Event) =>
          onChange((event.target as HTMLSelectElement).value),
      },
      h("option", { value: "" }, "— none —"),
      ...options.map((option) =>
        h(
          "option",
          option.id === value
            ? { value: option.id, selected: "" }
            : { value: option.id },
          option.label,
        ),
      ),
    );
    wrap.append(select);
  } else {
    wrap.append(
      h("input", {
        type: "text",
        value,
        placeholder,
        "data-ref": refId,
        oninput: (event: Event) =>
          onChange((event.target as HTMLInputElement).value),
      }),
    );
  }
  return wrap;
}

/**
 * Write the form's edits back to a filed finding.
 *
 * Two calls, because they are two different permissions and the split is the
 * module's inviolable rule 2: the wording goes through `correctFinding`, which
 * only the AUTHOR may call and which keeps the superseded version as a
 * revision; severity and the reviewer comment go through `patchFinding`, which
 * a reviewer may call and which has no field for the tester's words at all.
 * Each is skipped when nothing in its half changed, so an edit of one does not
 * stamp `edited_at` on the other.
 */
async function saveFindingEdits(finding: Finding): Promise<void> {
  if (!draft.observed.trim()) {
    state.error = "Observed is required.";
    render();
    return;
  }

  const observed = draft.observed.trim();
  const expected = draft.expected.trim() || null;
  const comment = draft.comment.trim() || null;
  const isAuthor = finding.session.tester === state.config?.tester;

  try {
    let updated = finding;
    if (
      isAuthor &&
      (observed !== finding.observed || expected !== finding.expected)
    ) {
      ({ finding: updated } = await api.correctFinding(
        finding.id,
        observed,
        expected,
      ));
    }
    if (draft.severity !== finding.severity || comment !== finding.comment) {
      updated = await api.patchFinding(finding.id, {
        severity: draft.severity,
        comment,
      });
    }
    replaceFinding(updated);

    // A finding nobody filed yet can be filed as part of the same save — the
    // same checkbox, in the same place, as when it was written.
    if (!finding.issue && draft.fileToTracker) {
      const result = await api.fileIssue(finding.id, targetSelection());
      replaceFinding(result.finding);
      flashStatus(`saved · filed as #${result.issue.id}`);
    } else {
      flashStatus("saved");
    }
    state.error = null;
    finishEditing();
  } catch (error) {
    state.error =
      error instanceof Error ? error.message : "Could not save changes";
    render();
  }
}

/**
 * Move an already-filed work item to a different sprint, story or assignee.
 *
 * Its own button rather than part of Save: the tracker fields are pre-filled
 * from what this project filed against LAST, not from where this work item
 * actually sits, so saving them on every edit would silently re-target a bug
 * whose sprint the tester never looked at.
 */
async function retargetIssue(finding: Finding): Promise<void> {
  if (!finding.issue) return;
  try {
    await api.updateIssueTargets(finding.id, targetSelection());
    flashStatus(`updated #${finding.issue.id}`);
    state.error = null;
  } catch (error) {
    state.error =
      error instanceof Error ? error.message : "Could not update work item";
  }
  render();
}

// ------------------------------------------------------- all findings --

const ALL_REF = "QaDeck:All";

const ALL_FILTERS: { filter: AllFilter; label: string }[] = [
  { filter: "needs_me", label: "Needs me" },
  { filter: "open", label: "Open" },
  { filter: "fixed", label: "Awaiting retest" },
  { filter: "resolved", label: "Resolved" },
  { filter: "wont_fix", label: "Won't fix" },
  { filter: "everything", label: "All" },
];

/** The statuses one filter reads. `needs_me` adds the answers on top. */
function statusesFor(filter: AllFilter): (Finding["status"] | undefined)[] {
  switch (filter) {
    case "needs_me":
      return ["needs_discussion", "fixed"];
    case "open":
      return ["open", "filed"];
    case "everything":
      return [undefined];
    default:
      return [filter];
  }
}

/** The server's cap on one list read (`GET_findings`). */
const LIST_CAP = 200;
let allSeq = 0;

/**
 * Load the "All findings" list for the current filter and search.
 *
 * A `QA-142` (or `142`, or a `fnd_` id) is looked up directly — the name a
 * tester says out loud has to find its card in one step. Anything else goes
 * to the server's text search, within the filter. Last request wins: a slow
 * answer to an older filter must not paint over the one now selected.
 */
async function loadAll(): Promise<void> {
  const seq = ++allSeq;
  const all = state.all;
  all.loading = true;
  all.error = null;
  render();
  try {
    const q = all.query.trim();
    const ref = parseFindingRef(q);
    let list: Finding[] = [];
    let capped = false;
    if (ref || /^fnd_[A-Za-z0-9_-]+$/.test(q)) {
      try {
        const { finding } = await api.getFinding(ref ? `QA-${ref}` : q);
        list = [finding];
      } catch (error) {
        if (!(error instanceof api.ApiClientError && error.status === 404)) {
          throw error;
        }
      }
    } else {
      const reads = statusesFor(all.filter).map((status) =>
        api.listProjectFindings({ status, q: q || undefined }),
      );
      // "Need answer" — no status names it, so it is its own read: open
      // findings where the last word is not the tester's (§7i). The list, not
      // the inbox's ids: those are capped at a page, and a tester with sixty
      // unanswered questions must be able to reach the sixtieth.
      if (all.filter === "needs_me") {
        reads.push(
          api.listProjectFindings({ awaitingTester: true, q: q || undefined }),
        );
      }
      const results = await Promise.all(reads);
      const seen = new Set<string>();
      for (const result of results) {
        const rows = wireList(result.findings);
        if (rows.length >= LIST_CAP) capped = true;
        for (const finding of rows) {
          if (seen.has(finding.id)) continue;
          seen.add(finding.id);
          list.push(finding);
        }
      }
      if (all.filter !== "needs_me") {
        list.sort((a, b) => b.created_at.localeCompare(a.created_at));
      }
    }
    if (seq !== allSeq) return;
    all.findings = list;
    all.capped = capped;
    all.loaded = true;
  } catch (error) {
    if (seq !== allSeq) return;
    all.error =
      error instanceof Error ? error.message : "Could not load the findings";
  } finally {
    if (seq === allSeq) {
      all.loading = false;
      render();
    }
  }
}

/**
 * "All findings" — every finding in the project, from any page.
 *
 * The tab that makes nothing unreachable: the strip can be dismissed and the
 * page tab lists only the page, but here every finding is a filter and a
 * click away, and every card carries "Change status". Rows are compact and
 * open into the full card, because a project holds hundreds.
 */
function renderAll(): Node {
  const all = state.all;
  const wrap = h("div", { "data-ref": `${ALL_REF}:view` });
  const inbox = renderInbox({ rowsHere: all.filter !== "needs_me" });
  if (inbox) wrap.append(inbox);

  const chips = h("div", { class: "row all-filters" });
  const needsMe = inboxCount();
  for (const { filter, label } of ALL_FILTERS) {
    chips.append(
      h(
        "button",
        {
          class: `btn${all.filter === filter ? " active" : ""}`,
          type: "button",
          "data-ref": `${ALL_REF}:filter-${filter}`,
          onclick: () => {
            all.filter = filter;
            all.kind = null;
            all.open.clear();
            void loadAll();
          },
        },
        filter === "needs_me" && needsMe ? `${label} (${needsMe})` : label,
      ),
    );
  }
  wrap.append(chips);

  const search = h("input", {
    type: "search",
    placeholder: "Search the words, or QA-142",
    "data-ref": `${ALL_REF}:search`,
  }) as HTMLInputElement;
  search.value = all.query;
  search.addEventListener("input", () => {
    all.query = search.value;
  });
  search.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      all.open.clear();
      void loadAll();
    }
  });
  // A cleared box (the ✕ a search field draws) is "show the filter again".
  search.addEventListener("search", () => {
    if (!search.value) void loadAll();
  });
  wrap.append(search);

  wrap.append(
    h(
      "p",
      { class: "note" },
      "Pressed something by mistake? Open the finding and use Change status — you can always change it back.",
    ),
  );

  if (all.error) wrap.append(h("div", { class: "error" }, all.error));
  if (all.loading && !all.loaded) {
    wrap.append(h("p", { class: "note" }, "Loading…"));
    return wrap;
  }
  if (all.filter === "needs_me") {
    wrap.append(renderNeedsMe(all.findings));
  } else {
    if (!all.findings.length) {
      wrap.append(
        h(
          "p",
          { class: "note" },
          all.query.trim() ? "Nothing matches that." : "None.",
        ),
      );
    }
    for (const finding of all.findings) wrap.append(renderAllRow(finding));
  }
  if (all.capped) {
    wrap.append(
      h(
        "p",
        { class: "note" },
        `Showing the newest ${LIST_CAP} — search to find an older one.`,
      ),
    );
  }
  return wrap;
}

/** One compact row; pressed, it opens into the full card underneath. */
function renderAllRow(finding: Finding): Node {
  const open = state.all.open.has(finding.id);
  const ref = formatFindingRef(finding.ref);
  const firstLine = finding.observed.split("\n")[0] ?? "";
  const row = h("div", {
    class: `all-row${open ? " open" : ""}`,
    "data-ref": `${ALL_REF}:row-${finding.id}`,
  });
  row.append(
    h(
      "button",
      {
        class: "all-head",
        type: "button",
        title: open ? "Fold this finding" : "Open this finding",
        "data-ref": `${ALL_REF}:toggle-${finding.id}`,
        onclick: () => {
          if (open) state.all.open.delete(finding.id);
          else state.all.open.add(finding.id);
          render();
        },
      },
      h("span", { class: "caret" }, open ? "▾" : "▸"),
      ref ? h("span", { class: "tag ref" }, ref) : null,
      h(
        "span",
        { class: `tag status-${finding.status}` },
        finding.status.replace(/_/g, " "),
      ),
      h(
        "span",
        { class: "all-title" },
        firstLine.length > 90 ? `${firstLine.slice(0, 90)}…` : firstLine,
      ),
    ),
  );
  row.append(
    h(
      "div",
      { class: "all-meta" },
      renderTurnPill(finding),
      h("span", { class: "ref" }, `on ${finding.route_key}`),
    ),
  );
  if (open) row.append(renderFinding(finding));
  return row;
}

// ------------------------------------------------------------ the turn --

/**
 * Whose move it is on one finding, if it is the tester's.
 *
 * A fix claim is a retest and a question is a decision, whoever spoke last.
 * Anything else open is the tester's turn exactly when the newest message is
 * someone else's (`awaiting_tester`) — read or not, because the turn passes
 * when the tester ACTS. Read through `??`: an older server never sends it.
 */
function turnOf(finding: Finding): TurnKind | null {
  if (finding.status === "fixed") return "retest";
  if (finding.status === "needs_discussion") return "decide";
  if (
    (finding.status === "open" || finding.status === "filed") &&
    (finding.awaiting_tester ?? null)
  ) {
    return "answer";
  }
  return null;
}

const TURNS: { kind: TurnKind; title: string; count: string }[] = [
  { kind: "answer", title: "Need answer", count: "to answer" },
  { kind: "decide", title: "Decide", count: "to decide" },
  { kind: "retest", title: "Retest", count: "to retest" },
];

/** What is waiting, by whom, since when — the line a job leads with. */
function turnAsk(
  finding: Finding,
  kind: TurnKind,
): { who: string; verb: string; at: string | null; text: string | null } {
  const waiting = finding.awaiting_tester ?? null;
  switch (kind) {
    case "retest":
      return {
        who: "Fix claimed",
        verb: "",
        at: finding.fixed_at ?? null,
        text: finding.fix_note ?? null,
      };
    case "decide":
      return {
        who: finding.discussion_asked_by ?? waiting?.author ?? "A developer",
        verb: " wants your view",
        at: finding.discussion_at ?? waiting?.at ?? null,
        text: finding.discussion_question ?? waiting?.body ?? null,
      };
    case "answer":
      return {
        who: waiting?.author ?? "A developer",
        verb: " asked you",
        at: waiting?.at ?? null,
        text: waiting?.body ?? null,
      };
  }
}

/** The clock a job is sorted by — newest first within its group. */
function turnClock(finding: Finding, kind: TurnKind): string {
  return turnAsk(finding, kind).at ?? finding.created_at;
}

/**
 * The pill on a compact row, in every filter: why the finding is waiting on
 * the tester, or — when it is not — that there is a conversation and who
 * has the next word. A row that said nothing about its thread is how a
 * developer's question sat unseen in a list of forty.
 */
function renderTurnPill(finding: Finding): Node | null {
  const kind = turnOf(finding);
  if (kind) {
    const ask = turnAsk(finding, kind);
    const label =
      kind === "retest"
        ? "Retest"
        : kind === "decide"
          ? "Question for you"
          : `${ask.who} asked you`;
    return h(
      "span",
      {
        class: `pill ${TURN_PILL[kind]}`,
        "data-ref": `${ALL_REF}:turn-${finding.id}`,
      },
      ask.at ? `${label} · ${sinceLabel(ask.at)}` : label,
    );
  }
  const count = finding.comments_count ?? 0;
  if (!count) return null;
  return h(
    "span",
    { class: "pill" },
    `${count} ${count === 1 ? "message" : "messages"} · waiting on the team`,
  );
}

/** The strip's pill class for each job — one palette across the panel. */
const TURN_PILL: Record<TurnKind, string> = {
  answer: "reply",
  decide: "discussion",
  retest: "retest",
};

/**
 * "Needs me" — a to-do list grouped by the job, not a filtered list.
 *
 * Leads with how many things are waiting, and the three counts under it are
 * the filter. Each job quotes what is waiting and carries its own verb, so
 * most turns are taken without opening anything; pressed, the row opens into
 * the full card. A finding answered here stays on screen under "Answered"
 * until the list reloads — the undo is on its card, so it must not vanish.
 */
/**
 * The loaded "Needs me" findings, sorted into the three jobs (newest first
 * within each) plus the ones answered since the list loaded. One function,
 * because the list and its "See all" popup must agree row for row.
 */
function needsMeGroups(findings: Finding[]): {
  groups: Map<TurnKind, Finding[]>;
  answered: Finding[];
} {
  const groups = new Map<TurnKind, Finding[]>(
    TURNS.map(({ kind }) => [kind, []]),
  );
  const answered: Finding[] = [];
  for (const finding of findings) {
    const kind = turnOf(finding);
    if (kind) groups.get(kind)!.push(finding);
    else answered.push(finding);
  }
  for (const [kind, list] of groups) {
    list.sort((a, b) => turnClock(b, kind).localeCompare(turnClock(a, kind)));
  }
  return { groups, answered };
}

function renderNeedsMe(findings: Finding[]): Node {
  const all = state.all;
  const { groups, answered } = needsMeGroups(findings);
  const total = [...groups.values()].reduce((n, list) => n + list.length, 0);

  const wrap = h("div", { "data-ref": `${ALL_REF}:needs-me` });
  const head = h(
    "div",
    { class: "turn-head" },
    h(
      "div",
      { class: "turn-big", "data-ref": `${ALL_REF}:waiting-count` },
      total
        ? `${total} ${total === 1 ? "thing is" : "things are"} waiting on you`
        : "Nothing is waiting on you",
    ),
  );
  if (total) {
    const seg = h("div", { class: "turn-seg" });
    for (const { kind, count } of TURNS) {
      const n = groups.get(kind)!.length;
      seg.append(
        h(
          "button",
          {
            class: `turn-${kind}${all.kind === kind ? " on" : ""}`,
            type: "button",
            title: all.kind === kind ? "Show all three again" : `Only ${count}`,
            "data-ref": `${ALL_REF}:kind-${kind}`,
            disabled: !n && all.kind !== kind,
            onclick: () => {
              all.kind = all.kind === kind ? null : kind;
              render();
            },
          },
          h("span", { class: "num" }, String(n)),
          h("span", { class: "lb" }, count),
        ),
      );
    }
    head.append(seg);
  }
  wrap.append(head);

  for (const { kind, title } of TURNS) {
    if (all.kind && all.kind !== kind) continue;
    const list = groups.get(kind)!;
    if (!list.length) continue;
    wrap.append(
      kind === "decide"
        ? renderTurnGroup(kind, title, list, (finding) =>
            renderTurnRow(finding, kind),
          )
        : renderPagedGroup(kind, title, list),
    );
  }

  if (answered.length && !all.kind) {
    wrap.append(
      renderTurnGroup(
        "answered",
        "Answered — waiting on the team",
        answered,
        renderAllRow,
      ),
    );
  }

  wrap.append(
    h(
      "button",
      {
        class: "turn-rest",
        type: "button",
        "data-ref": `${ALL_REF}:everything-else`,
        onclick: () => {
          all.filter = "everything";
          all.kind = null;
          all.open.clear();
          void loadAll();
        },
      },
      h("span", {}, "Everything else in the project"),
      h("span", { class: "note" }, "All ▸"),
    ),
  );
  return wrap;
}

/** Rows a group shows before "Show all" — enough to start on, few enough
 *  that three groups still fit on one screen. */
const TURN_GROUP_PREVIEW = 5;

/**
 * One group of "Needs me": a heading that folds it, the first few rows, and
 * "Show all n" for the rest. Fifty retests and fifty answers is an ordinary
 * week, and a list that renders all of them open is a list nobody scrolls.
 * A group picked from the header shows whole — that pick IS asking for all.
 */
function renderTurnGroup(
  key: TurnKind | "answered",
  title: string,
  list: Finding[],
  row: (finding: Finding) => Node,
): Node {
  const all = state.all;
  const folded = all.folded.has(key);
  const whole = all.kind === key || all.showAll.has(key);
  const wrap = h("div", { "data-ref": `${ALL_REF}:group-${key}` });
  wrap.append(
    h(
      "button",
      {
        class: `turn-group${key === "answered" ? "" : ` turn-${key}`}`,
        type: "button",
        title: folded ? `Show ${title.toLowerCase()}` : "Fold this group",
        "data-ref": `${ALL_REF}:fold-${key}`,
        onclick: () => {
          if (folded) all.folded.delete(key);
          else all.folded.add(key);
          render();
        },
      },
      h("span", { class: "caret" }, folded ? "▸" : "▾"),
      h("span", { class: "h" }, title),
      h("span", { class: "n" }, String(list.length)),
      h("span", { class: "rule" }),
    ),
  );
  if (folded) return wrap;

  const shown = whole ? list : list.slice(0, TURN_GROUP_PREVIEW);
  for (const finding of shown) wrap.append(row(finding));
  if (list.length > TURN_GROUP_PREVIEW && all.kind !== key) {
    wrap.append(
      h(
        "button",
        {
          class: "turn-more",
          type: "button",
          "data-ref": `${ALL_REF}:more-${key}`,
          onclick: () => {
            if (whole) all.showAll.delete(key);
            else all.showAll.add(key);
            render();
          },
        },
        whole ? "Show fewer" : `Show all ${list.length}`,
      ),
    );
  }
  return wrap;
}

/** Does this job match a search? Its QA number, the tester's words, and who
 *  is waiting and what they said — what a tester remembers it by. */
function turnMatches(finding: Finding, kind: TurnKind, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const ask = turnAsk(finding, kind);
  return [
    formatFindingRef(finding.ref) ?? "",
    finding.observed,
    ask.who,
    ask.text ?? "",
  ]
    .join("\n")
    .toLowerCase()
    .includes(q);
}

/**
 * "Need answer" / "Retest": ten rows, "Load 10 more", "See all" (a popup a
 * hundred at a time), and a search that opens from the heading.
 *
 * These are the two that pile up. Ten is a screen; more comes when it is
 * asked for, never by default — the list sits above everything else on the
 * tab, and a list that renders all of them is a list nobody scrolls past.
 */
function renderPagedGroup(
  kind: PagedTurn,
  title: string,
  list: Finding[],
): Node {
  const all = state.all;
  const view = all.lists[kind];
  const folded = all.folded.has(kind);
  const wrap = h("div", { "data-ref": `${ALL_REF}:group-${kind}` });

  const head = h("div", { class: `turn-group-head turn-${kind}` });
  head.append(
    h(
      "button",
      {
        class: "turn-group",
        type: "button",
        title: folded ? `Show ${title.toLowerCase()}` : "Fold this group",
        "data-ref": `${ALL_REF}:fold-${kind}`,
        onclick: () => {
          if (folded) all.folded.delete(kind);
          else all.folded.add(kind);
          render();
        },
      },
      h("span", { class: "caret" }, folded ? "▸" : "▾"),
      h("span", { class: "h" }, title),
      h("span", { class: "n" }, String(list.length)),
      h("span", { class: "rule" }),
    ),
    h(
      "button",
      {
        class: `turn-search-toggle${view.searching ? " on" : ""}`,
        type: "button",
        title: view.searching
          ? "Close the search"
          : `Search ${title.toLowerCase()}`,
        "data-ref": `${ALL_REF}:search-open-${kind}`,
        onclick: () => {
          view.searching = !view.searching;
          if (!view.searching) view.query = "";
          all.folded.delete(kind);
          render();
          if (view.searching) {
            root
              .querySelector<HTMLInputElement>(
                `[data-ref="${ALL_REF}:search-${kind}"]`,
              )
              ?.focus();
          }
        },
      },
      view.searching ? "✕" : "Search",
    ),
  );
  wrap.append(head);
  if (folded) return wrap;

  if (view.searching) {
    const input = h("input", {
      type: "search",
      class: "turn-search",
      placeholder: "Words, a name, or QA-142",
      "data-ref": `${ALL_REF}:search-${kind}`,
    }) as HTMLInputElement;
    input.value = view.query;
    input.addEventListener("input", () => {
      view.query = input.value;
      view.limit = PAGED_STEP;
      renderKeepingFocus();
    });
    input.addEventListener("keydown", (event) => {
      if ((event as KeyboardEvent).key === "Escape") {
        view.searching = false;
        view.query = "";
        render();
      }
    });
    wrap.append(input);
  }

  const matches = list.filter((finding) =>
    turnMatches(finding, kind, view.query),
  );
  if (view.query.trim() && !matches.length) {
    wrap.append(h("p", { class: "note" }, "Nothing here matches that."));
  }
  for (const finding of matches.slice(0, view.limit)) {
    wrap.append(renderTurnRow(finding, kind));
  }

  const more = h("div", { class: "turn-more-row" });
  if (matches.length > view.limit) {
    const next = Math.min(PAGED_STEP, matches.length - view.limit);
    more.append(
      h(
        "button",
        {
          class: "turn-more",
          type: "button",
          "data-ref": `${ALL_REF}:more-${kind}`,
          onclick: () => {
            view.limit += PAGED_STEP;
            render();
          },
        },
        `Load ${next} more`,
      ),
    );
  }
  if (list.length > PAGED_STEP) {
    more.append(
      h(
        "button",
        {
          class: "turn-more",
          type: "button",
          "data-ref": `${ALL_REF}:see-all-${kind}`,
          onclick: () => {
            all.popup = { kind, page: 0, query: view.query };
            render();
            root
              .querySelector<HTMLInputElement>(
                `[data-ref="${ALL_REF}:popup-search"]`,
              )
              ?.focus();
          },
        },
        `See all ${list.length}`,
      ),
    );
  }
  if (more.childNodes.length) wrap.append(more);
  return wrap;
}

/**
 * "See all" — one group in a popup over the page: wider than the panel,
 * scrollable, and a hundred rows a page so even a backlog renders at once.
 * Pressing a row closes the popup and unfolds that row in the list, loading
 * as far down as it sits, so the job is done where every other one is.
 */
function renderTurnPopup(
  popup: NonNullable<typeof state.all.popup>,
  title: string,
  list: Finding[],
): Node {
  const all = state.all;
  const kind = popup.kind;
  const close = () => {
    all.popup = null;
    render();
  };
  const matches = list.filter((finding) =>
    turnMatches(finding, kind, popup.query),
  );
  const pages = Math.max(1, Math.ceil(matches.length / POPUP_PAGE));
  const page = Math.min(popup.page, pages - 1);
  const from = page * POPUP_PAGE;
  const shown = matches.slice(from, from + POPUP_PAGE);

  const search = h("input", {
    type: "search",
    class: "turn-search",
    placeholder: "Words, a name, or QA-142",
    "data-ref": `${ALL_REF}:popup-search`,
  }) as HTMLInputElement;
  search.value = popup.query;
  search.addEventListener("input", () => {
    popup.query = search.value;
    popup.page = 0;
    renderKeepingFocus();
  });

  const body = h("div", {
    class: "popup-list",
    "data-ref": `${ALL_REF}:popup-list`,
  });
  if (!shown.length) {
    body.append(h("p", { class: "note" }, "Nothing here matches that."));
  }
  for (const finding of shown) {
    const ask = turnAsk(finding, kind);
    const ref = formatFindingRef(finding.ref);
    body.append(
      h(
        "button",
        {
          class: `popup-row turn-${kind}`,
          type: "button",
          title: "Show it in the list",
          "data-ref": `${ALL_REF}:popup-row-${finding.id}`,
          onclick: () => {
            const view = all.lists[kind];
            // Unfold it where it lives: no search hiding it, and loaded as
            // far down as it sits.
            view.query = "";
            view.searching = false;
            const at = list.findIndex((f) => f.id === finding.id);
            view.limit = Math.max(
              view.limit,
              Math.ceil((at + 1) / PAGED_STEP) * PAGED_STEP,
            );
            all.folded.delete(kind);
            all.peek = finding.id;
            all.popup = null;
            render();
            root
              .querySelector<HTMLElement>(
                `[data-ref="${ALL_REF}:row-${CSS.escape(finding.id)}"]`,
              )
              ?.scrollIntoView({ block: "center" });
          },
        },
        h(
          "span",
          { class: "popup-line" },
          ref ? h("span", { class: "tag ref" }, ref) : null,
          h(
            "span",
            { class: "all-title" },
            finding.observed.split("\n")[0] ?? "",
          ),
          h(
            "span",
            { class: "turn-when" },
            ask.at ? sinceLabel(ask.at).replace(" ago", "") : "",
          ),
        ),
        ask.text
          ? h("span", { class: "popup-quote" }, `${ask.who}: ${ask.text}`)
          : null,
      ),
    );
  }

  const pager = h(
    "div",
    { class: "popup-foot" },
    h(
      "span",
      { class: "note" },
      matches.length
        ? `${from + 1}–${from + shown.length} of ${matches.length}`
        : "0",
    ),
    h("span", { style: "flex:1" }),
    h(
      "button",
      {
        class: "btn",
        type: "button",
        disabled: page === 0,
        "data-ref": `${ALL_REF}:popup-prev`,
        onclick: () => {
          popup.page = page - 1;
          render();
        },
      },
      "‹ Previous 100",
    ),
    h(
      "button",
      {
        class: "btn",
        type: "button",
        disabled: page >= pages - 1,
        "data-ref": `${ALL_REF}:popup-next`,
        onclick: () => {
          popup.page = page + 1;
          render();
        },
      },
      "Next 100 ›",
    ),
  );

  const dialog = h(
    "div",
    {
      class: `popup turn-${kind}`,
      role: "dialog",
      "aria-label": title,
      "data-ref": `${ALL_REF}:popup`,
    },
    h(
      "div",
      { class: "popup-head" },
      h("span", { class: "popup-title" }, title),
      h("span", { class: "n" }, String(list.length)),
      h("span", { style: "flex:1" }),
      h(
        "button",
        {
          class: "icon",
          type: "button",
          title: "Close",
          "data-ref": `${ALL_REF}:popup-close`,
          onclick: close,
        },
        "✕",
      ),
    ),
    search,
    body,
    pager,
  );
  const overlay = h("div", { class: "popup-overlay" }, dialog);
  // A press on the backdrop closes it; one inside the dialog does not.
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) close();
  });
  overlay.addEventListener("keydown", (event) => {
    if ((event as KeyboardEvent).key === "Escape") close();
  });
  return overlay;
}

/**
 * One job, as ONE line: what the finding is and who is waiting since when.
 *
 * Pressed, it unfolds — one row at a time — to the message and the verb that
 * answers it, so most turns are still taken without opening anything; "Open
 * card" (or Reply) opens the full card, whose first block is that same job.
 */
function renderTurnRow(finding: Finding, kind: TurnKind): Node {
  const all = state.all;
  const open = all.open.has(finding.id);
  const peek = open || all.peek === finding.id;
  const ref = formatFindingRef(finding.ref);
  const firstLine = finding.observed.split("\n")[0] ?? "";
  const ask = turnAsk(finding, kind);
  const openCard = () => {
    all.open.add(finding.id);
    all.peek = finding.id;
    render();
  };

  const card = h("div", {
    class: `all-row turn-card turn-${kind}${peek ? " peek" : ""}${open ? " open" : ""}`,
    "data-ref": `${ALL_REF}:row-${finding.id}`,
  });
  card.append(
    h(
      "button",
      {
        class: "all-head",
        type: "button",
        title: peek ? "Fold" : "Show the message and what to do",
        "data-ref": `${ALL_REF}:toggle-${finding.id}`,
        onclick: () => {
          if (peek) {
            all.open.delete(finding.id);
            all.peek = null;
          } else {
            all.peek = finding.id;
          }
          render();
        },
      },
      h("span", { class: "caret" }, peek ? "▾" : "▸"),
      ref ? h("span", { class: "tag ref" }, ref) : null,
      h(
        "span",
        { class: "all-title" },
        firstLine.length > 90 ? `${firstLine.slice(0, 90)}…` : firstLine,
      ),
      h(
        "span",
        { class: "turn-when" },
        ask.at ? sinceLabel(ask.at).replace(" ago", "") : "",
      ),
    ),
  );
  if (open) {
    card.append(renderFinding(finding));
    return card;
  }
  if (!peek) return card;

  card.append(
    h(
      "div",
      { class: "turn-who" },
      h("span", { class: "av" }, (ask.who.trim()[0] ?? "?").toUpperCase()),
      h(
        "span",
        {},
        h("b", {}, ask.who),
        `${ask.verb}${ask.at ? ` · ${sinceLabel(ask.at)}` : ""}`,
      ),
    ),
  );
  if (ask.text) card.append(h("div", { class: "turn-quote" }, ask.text));

  const acts = h("div", { class: "row turn-acts" });
  if (kind === "answer") {
    acts.append(
      h(
        "button",
        {
          class: "btn primary",
          type: "button",
          "data-ref": `${ALL_REF}:reply-${finding.id}`,
          onclick: () => {
            openCard();
            root
              .querySelector<HTMLTextAreaElement>(
                `[data-ref="${ASK_REF}:input-${CSS.escape(finding.id)}"]`,
              )
              ?.focus();
          },
        },
        "Reply",
      ),
    );
  } else if (kind === "decide") {
    const busy = answerDrafts.get(finding.id)?.sending ?? false;
    acts.append(
      h(
        "button",
        {
          class: "btn primary",
          type: "button",
          title: "Agree — the developer goes ahead",
          "data-ref": `${ALL_REF}:agree-${finding.id}`,
          disabled: busy,
          onclick: () => void sendDiscussionAnswer(finding, "agree"),
        },
        "✓ Go ahead",
      ),
      h(
        "button",
        {
          class: "btn",
          type: "button",
          title: "Not needed — the finding is closed as won't fix",
          "data-ref": `${ALL_REF}:drop-${finding.id}`,
          disabled: busy,
          onclick: () => void sendDiscussionAnswer(finding, "drop"),
        },
        "✕ Not needed",
      ),
      h(
        "button",
        {
          class: "btn",
          type: "button",
          title: "Open the card to answer in words",
          onclick: openCard,
        },
        "Reply",
      ),
    );
  } else {
    const retest = retestButton({ id: finding.id, url: finding.url }, true);
    if (retest) acts.append(retest);
  }
  acts.append(
    h("span", { class: "spacer", style: "flex:1" }),
    h(
      "button",
      {
        class: "btn",
        type: "button",
        title: "The whole finding — your report, the thread, Change status",
        "data-ref": `${ALL_REF}:open-${finding.id}`,
        onclick: openCard,
      },
      "Open card",
    ),
  );
  card.append(acts);
  const error = answerDrafts.get(finding.id)?.error ?? null;
  if (kind === "decide" && error) {
    card.append(h("div", { class: "error" }, error));
  }
  return card;
}

// ------------------------------------------------------- change status --

const STATUS_CHOICES: { status: TesterStatus; label: string }[] = [
  { status: "open", label: "Open — still a problem" },
  { status: "resolved", label: "Resolved — works now" },
  { status: "wont_fix", label: "Won't fix — not needed" },
  { status: "needs_discussion", label: "Back to the question" },
];

/** Findings with a status change in flight — one at a time per card. */
const statusBusy = new Set<string>();

/**
 * "Change status" — on every card, whatever its status.
 *
 * The tester's undo for any verdict they gave: ✓ on a retest, Go ahead / Not
 * needed on a question, a resolve. Only the tester's own verdicts are offered
 * (`TesterStatus`): never `fixed`, which is the developer's claim. "Back to the
 * question" only where a question was asked, so the status never exists
 * without one. Every change is said in the finding's thread by the server.
 */
function renderStatusControl(finding: Finding): Node {
  const select = h("select", {
    class: "status-select",
    title: "Change the status — you can always change it back",
    "data-ref": `QaDeck:Finding:status-${finding.id}`,
  }) as HTMLSelectElement;
  select.append(h("option", { value: "" }, "Change status…"));
  for (const choice of STATUS_CHOICES) {
    if (choice.status === finding.status) continue;
    // With a work item, "open" IS `filed` — offering it there changes nothing.
    if (choice.status === "open" && finding.status === "filed") continue;
    if (choice.status === "needs_discussion" && !finding.discussion_at) {
      continue;
    }
    select.append(h("option", { value: choice.status }, choice.label));
  }
  if (statusBusy.has(finding.id)) select.disabled = true;
  select.addEventListener("change", () => {
    const next = select.value as TesterStatus | "";
    select.value = "";
    if (next) void changeStatus(finding, next);
  });
  return select;
}

async function changeStatus(
  finding: Finding,
  status: TesterStatus,
): Promise<void> {
  if (statusBusy.has(finding.id)) return;
  statusBusy.add(finding.id);
  state.error = null;
  render();
  try {
    const { finding: updated } = await api.setTesterStatus(
      finding.id,
      finding.status,
      status,
    );
    replaceFinding(updated);
    if (finding.status === "fixed") {
      // The claim is answered, as the answer block would have: the strip row
      // goes, and a held retest pin is released — but the CARD stays, new
      // status showing, so a mis-click is undone where it happened.
      clearInboxRetest(finding.id);
      api.releaseFindingFocus(finding.id);
      api.releaseFindingFocus(updated.id);
      if (state.focus?.finding?.id === updated.id) state.focus.retest = false;
    }
    if (finding.status === "needs_discussion") {
      clearInboxDiscussion(finding.id);
      clearInboxReply(finding.id);
    }
    // A status change is an act, echoed in the thread: the turn has passed.
    clearInboxAnswer(finding.id);
    const view = state.threads.get(finding.id);
    if (view) {
      const { comments } = await api.listFindingComments(finding.id);
      view.comments = wireList(comments);
    }
    flashStatus(
      `Now ${updated.status.replace(/_/g, " ")} — you can change it back any time`,
    );
    void refreshInbox();
  } catch (error) {
    state.error =
      error instanceof Error ? error.message : "Could not change the status";
  } finally {
    statusBusy.delete(finding.id);
    render();
  }
}

/** Findings with an AI-fix request in flight — one at a time per card. */
const aiFixBusy = new Set<string>();

/**
 * "Ask for an AI fix" / "Fix with AI" — where the project lets a tester start
 * one (`config.fixStarter`), on a finding that is still open. Every ask has
 * its undo here too (§7h): a pending ask, or a fix this tester started that is
 * still waiting, can be withdrawn by the tester who made it. Once the fix is
 * claimed the card's answer block takes over — the preview link is there.
 */
function renderAiFixControl(finding: Finding): Node | null {
  const starter = state.config?.fixStarter ?? "dashboard";
  if (starter === "dashboard") return null;
  if (finding.status !== "open" && finding.status !== "filed") return null;
  const me = state.config?.tester;
  const busy = aiFixBusy.has(finding.id);
  const withdraw = (label: string) =>
    h(
      "button",
      {
        class: "btn",
        disabled: busy,
        "data-ref": `QaDeck:Finding:ai-fix-withdraw-${finding.id}`,
        onclick: () => void askAiFix(finding, false),
      },
      label,
    );

  if (finding.fix_requested_at) {
    const mine =
      finding.fix_requested_via === "tester" && finding.fix_requested_by === me;
    return h(
      "span",
      {
        class: "note",
        "data-ref": `QaDeck:Finding:ai-fix-queued-${finding.id}`,
      },
      "AI fix queued — a preview link appears here when it is ready. ",
      mine ? withdraw("Withdraw") : null,
    );
  }
  if (finding.fix_request_pending_at) {
    const mine = finding.fix_request_pending_by === me;
    return h(
      "span",
      {
        class: "note",
        "data-ref": `QaDeck:Finding:ai-fix-asked-${finding.id}`,
      },
      mine
        ? "You asked for an AI fix — waiting for approval. "
        : `${finding.fix_request_pending_by ?? "A tester"} asked for an AI fix. `,
      mine ? withdraw("Withdraw") : null,
    );
  }
  return h(
    "button",
    {
      class: "btn primary",
      disabled: busy,
      title:
        starter === "tester_direct"
          ? "An AI fixes this on a live preview you can try; you answer it here"
          : "Ask for an AI fix; an owner or admin approves it",
      "data-ref": `QaDeck:Finding:ai-fix-${finding.id}`,
      onclick: () => void askAiFix(finding, true),
    },
    starter === "tester_direct" ? "Fix with AI" : "Ask for an AI fix",
  );
}

async function askAiFix(finding: Finding, requested: boolean): Promise<void> {
  if (aiFixBusy.has(finding.id)) return;
  aiFixBusy.add(finding.id);
  state.error = null;
  render();
  try {
    const { finding: updated, outcome } = await api.requestAiFix(
      finding.id,
      requested,
    );
    replaceFinding(updated);
    flashStatus(
      outcome === "queued"
        ? "AI fix started — the preview link appears on this card when it is ready"
        : outcome === "requested"
          ? "Asked — an owner or admin approves it"
          : outcome === "withdrawn"
            ? "Withdrawn"
            : "Nothing to change",
    );
  } catch (error) {
    state.error =
      error instanceof Error ? error.message : "Could not ask for an AI fix";
  } finally {
    aiFixBusy.delete(finding.id);
    render();
  }
}

/** Exposed for the widget bootstrap (SPA route changes). */
export function currentRouteKey(): string {
  return toRouteKey(location.href, routeConfig);
}
